// v3 — saved toast, 4-tab settings
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import API_BASE from '../config';
import CreditUsage from './CreditUsage';
import ErrorBoundary from './ErrorBoundary';
import { HUB_UPGRADE_URL } from '../lib/creditBalance';
import {
  SMS_TRANSLATE_MODES, TRANSLATE_DOMAINS, TRANSLATE_LANGUAGES,
  applyLineTranslateFromServer, loadTranslateSettings, saveTranslateSettings,
} from '../lib/translateSettings';
import {
  formatInTimeZone,
  loadDisplayTimeZone,
  saveDisplayTimeZone,
  timezoneChoices,
} from '../lib/displayTimezone';
import { applyTheme as setThemeMode, getTheme } from '../lib/theme';

// Native-platform detection, mirroring Login.js's implementation exactly.
// Login.js is the canonical reference and is intentionally NOT modified here;
// this is a byte-identical local copy so the two files cannot drift apart. If
// these were ever to need a shared source of truth, extract into a shared lib.
function safePlatform() {
  try {
    return Capacitor.getPlatform() || 'web';
  } catch (_) {
    return 'web';
  }
}

const SLOT_ROW = { display:'flex', alignItems:'center', gap:6, marginBottom:6, flexWrap:'nowrap' };
const SLOT_TIME = { flex:1, minWidth:0, minHeight:0, marginBottom:0, padding:'6px 8px', fontSize:'0.85rem' };
const SLOT_BTN = { flex:'none', width:32, height:32, borderRadius:8, border:'none', cursor:'pointer', fontSize:'1.1rem', lineHeight:1, backgroundColor:'var(--truva-btn-secondary-bg)', color:'var(--truva-text)' };

// "07:00","08:00" -> "7-8 AM"; "11:00","13:00" -> "11 AM-1 PM"; "07:30","08:30" -> "7:30-8:30 AM"
function slotLabel(start, end) {
  const p = (t) => { const [h, m] = (t || '').split(':').map(Number); return { h, m, ap: h >= 12 ? 'PM' : 'AM', h12: ((h + 11) % 12) + 1 }; };
  const a = p(start), b = p(end);
  if (Number.isNaN(a.h) || Number.isNaN(b.h)) return '';
  const f = (x) => (x.m ? `${x.h12}:${String(x.m).padStart(2, '0')}` : `${x.h12}`);
  return a.ap === b.ap ? `${f(a)}-${f(b)} ${b.ap}` : `${f(a)} ${a.ap}-${f(b)} ${b.ap}`;
}

function isNativePlatform() {
  try {
    if (typeof Capacitor.isNativePlatform === 'function') {
      return !!Capacitor.isNativePlatform();
    }
  } catch (_) { /* fall through */ }
  const p = safePlatform();
  return p === 'ios' || p === 'android';
}

// Same formatting convention as Contacts.js/Messages.js's displayNumber --
// used here so a number shown under its own friendly_name (itself often
// just a formatted number, e.g. "+1 (520) 614-1790") doesn't also show a
// second, differently-formatted (raw E.164) copy of the same digits below.
function displayPhoneNumber(num) {
  if (!num) return '';
  const d = num.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return num;
}

const HOURS_DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const HOURS_DAY_LABELS = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };
// Alerts tab: per-line email recipients + per-event toggles. The labels must
// stay in sync with backend/modules/alerts.py ALERT_EVENTS.
const ALERT_EVENT_LABELS = {
  missed_call: 'Missed call (caller hung up / went to voicemail)',
  voicemail: 'New voicemail',
  failed_transfer: 'Transfer to a human failed',
  chatbot_lead: 'Website chat visitor left contact details',
};
const ALERT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DEFAULT_HOURS_247 = () => HOURS_DAYS.reduce((acc, d) => {
  acc[d] = { open: '00:00', close: '23:59', closed: false };
  return acc;
}, {});
const DEFAULT_HOURS_MONFRI = () => HOURS_DAYS.reduce((acc, d) => {
  acc[d] = { open: '09:00', close: '17:00', closed: d === 'saturday' || d === 'sunday' };
  return acc;
}, {});
const hoursEqual = (a, b) => HOURS_DAYS.every(d => {
  const ad = (a && a[d]) || {}, bd = (b && b[d]) || {};
  return ad.open === bd.open && ad.close === bd.close && !!ad.closed === !!bd.closed;
});
// Which preset button (if any) matches the loaded hours — highlights the
// right button on load instead of always showing 24/7 as selected.
const fmtDateLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
// Monday of the week containing d - the attendant roster API's week_start.
const mondayOfWeek = (d) => {
  const mon = new Date(d);
  mon.setHours(0, 0, 0, 0);
  const day = mon.getDay(); // 0=Sun
  mon.setDate(mon.getDate() + (day === 0 ? -6 : 1 - day));
  return mon;
};
const detectHoursPreset = (bh, enforce) => {
  if (!enforce && hoursEqual(bh, DEFAULT_HOURS_247())) return '24/7';
  if (enforce && hoursEqual(bh, DEFAULT_HOURS_MONFRI())) return 'mon-fri';
  return 'custom';
};

export default function Settings({ authToken, phoneCount = 0, selectedNumber = '', user = null, narrowLayout = false, inPersonTranslateActive = false, onToggleInPersonTranslate = null, onAccountDeleted = null, notificationPermission = 'unknown' }) {
  const isAdmin = (() => {
    const r = String(user?.role || '').trim().toLowerCase();
    if (r === 'admin' || r === 'owner') return true;
    if (user?.is_admin === true) return true;
    // Sessions minted before role was stored: treat sole-account admins
    // as admin when role is missing (backend /api/me will refresh).
    return false;
  })();
  // Managers run Scheduling + Appointments (read and edit) but are not admins
  // anywhere else: no Team tab, no line settings, no billing.
  const isManager = String(user?.role || '').trim().toLowerCase() === 'manager';
  const maxSeatsFromPlan = (() => {
    const lim = user && user.limits;
    const n = Number(lim && (lim.max_seats || lim.seats));
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const showTeamTab = isAdmin && (phoneCount > 1 || maxSeatsFromPlan > 1);
  const showSchedulingTab = isAdmin || isManager;
    // Numbers & Lines is shown to everyone — admins self-serve (search/assign/
    // rename/close), non-admins see their assigned number(s) read-only so an
    // agent can understand why they don't have one yet and who to ask.
    const showNumbersTab = true;
  const canEdit = isAdmin;
  // Scheduling and Appointments: admins and managers can edit (the backend gate is _require_scheduling_access).
  const canEditScheduling = isAdmin || isManager;
  const [mode, setMode] = useState(null);
  const [offMode, setOffMode] = useState('forward'); // 'ring' | 'forward' when agent off
  const [error, setError] = useState('');
  const [translate, setTranslate] = useState(() => loadTranslateSettings(selectedNumber));
  const [translateSaved, setTranslateSaved] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(null);
  const [chatbotPrompt, setChatbotPrompt] = useState('');
  const [chatbotError, setChatbotError] = useState('');
  const [openAccount, setOpenAccount] = useState(false);
  const [deleteConfirming, setDeleteConfirming] = useState(false);
  const [acctDeleteBusy, setAcctDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [displayTz, setDisplayTz] = useState(() => loadDisplayTimeZone());

  // Agent config
  const [greeting, setGreeting] = useState('');
  const [transferNumber, setTransferNumber] = useState('');
  const [ownerCell, setOwnerCell] = useState('');
    const [transferMessage, setTransferMessage] = useState('');
      const [transferMessageMode, setTransferMessageMode] = useState('summary');
      const [transferTriggers, setTransferTriggers] = useState({caller_asks:true,sentiment_escalation:false,after_n_loops:2,business_hours_only:false});
  const [services, setServices] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState('');
  const [knowledgeDirty, setKnowledgeDirty] = useState(false);
  const [knowledgeSavedAt, setKnowledgeSavedAt] = useState(null);
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [voicePreference, setVoicePreference] = useState('rosie');
  const [afterHoursMessage, setAfterHoursMessage] = useState('');
  const [businessHours, setBusinessHours] = useState(() => DEFAULT_HOURS_247());
  const [enforceHours, setEnforceHours] = useState(false);
  const [hoursMode, setHoursMode] = useState('24/7'); // '24/7' | 'mon-fri' | 'custom'
  const [openRecording, setOpenRecording] = useState(false);
  const [recordingMode, setRecordingMode] = useState('off'); // 'off' | 'on_demand' | 'always'
  const [recordConsentAnnounce, setRecordConsentAnnounce] = useState(true); // server default is ON; never show it off before the line loads
  const [keepAudio, setKeepAudio] = useState(false);
  const [voicemailGreeting, setVoicemailGreeting] = useState('');
  const [locations, setLocations] = useState([]);
  const [activeLocId, setActiveLocId] = useState(null);
  const [maxLocations, setMaxLocations] = useState(1);
  const [newLocName, setNewLocName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [members, setMembers] = useState([]);
  const [numbersEditingFor, setNumbersEditingFor] = useState(null); // member id or null
  const [numberOptions, setNumberOptions] = useState([]); // [{id, e164, label}]
  const [selectedNumberIds, setSelectedNumberIds] = useState([]);
  const [numbersBusy, setNumbersBusy] = useState(false);
  const [openMemberMenu, setOpenMemberMenu] = useState(null); // member id or null
  // Fixed-position coords for the open member menu. The menu used to be
  // absolutely positioned inside the scrolling Team list, so the last rows'
  // menus (Make Agent / Remove...) were clipped by it.
  const [memberMenuPos, setMemberMenuPos] = useState(null); // {top?, bottom?, right}
  useEffect(() => {
    if (openMemberMenu === null) return undefined;
    const close = () => setOpenMemberMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openMemberMenu]);
  const [seatInfo, setSeatInfo] = useState({used:0,included:0,max:0});
  const [newService, setNewService] = useState({ name: '', description: '', price: '' });
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [resources, setResources] = useState([]); // scheduling staff/resources, each with .hours
  const [schedServices, setSchedServices] = useState([]); // scheduling service types (distinct from the JSON `services` list above)
  const [newResourceName, setNewResourceName] = useState('');
  const [newSchedService, setNewSchedService] = useState({ name: '', duration_minutes: 30, resource_ids: [] });
  const [editingHoursFor, setEditingHoursFor] = useState(null); // resource id or null
  const [schedulingEnabled, setSchedulingEnabled] = useState(false);
  // Attendant week roster: 4 fixed slots x 7 days, one resource per cell.
  const [rosterWeekStart, setRosterWeekStart] = useState(() => mondayOfWeek(new Date()));
  const [roster, setRoster] = useState(null); // {week_start, week_end, slots, days, cells, resources}
  const [rosterDraft, setRosterDraft] = useState({}); // `${date}|${slot_start}` -> resource id string or ''
  const [rosterSaving, setRosterSaving] = useState(false);
  // Copy one week of the roster onto other weeks. The inline panel keeps the
  // target list explicit: an admin sees exactly which weeks will be written
  // before confirming, because an overwrite replaces a whole week at once.
  const [rosterCopyOpen, setRosterCopyOpen] = useState(false);
  const [rosterCopyStart, setRosterCopyStart] = useState(''); // YYYY-MM-DD, first target Monday
  const [rosterCopyWeeks, setRosterCopyWeeks] = useState(1); // 1..12 consecutive weeks
  const [rosterCopyOverwrite, setRosterCopyOverwrite] = useState(false); // default OFF
  const [rosterCopying, setRosterCopying] = useState(false);
  const [rosterCopyResult, setRosterCopyResult] = useState(null); // {copied, skipped, inactive}
  // Editable slot template (tenant_configs.provider_meta.attendant_slots) - the
  // rows of the grid. An empty list is a real state: the admin cleared it.
  const [slotDraft, setSlotDraft] = useState([]);
  const [slotDraftLoaded, setSlotDraftLoaded] = useState(false);
  const [newSlot, setNewSlot] = useState({ label: '', start: '', end: '' });
  const [slotsSaving, setSlotsSaving] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [apptResourceFilter, setApptResourceFilter] = useState(null); // resource id or null = all
  const [apptWeekStart, setApptWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    const monOffset = day === 0 ? -6 : 1 - day;
    const mon = new Date(d);
    mon.setHours(0, 0, 0, 0);
    mon.setDate(d.getDate() + monOffset);
    return mon;
  });
  const [apptLoading, setApptLoading] = useState(false);
  const [gcal, setGcal] = useState(null); // {connected, email, calendar_id, oauth_configured}
  const [gcalBusy, setGcalBusy] = useState(false);
  const [rosieLangMode, setRosieLangMode] = useState('en_es');
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [hearing, setHearing] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [tab, setTab] = useState('business');
  const [theme, setTheme] = useState(() => getTheme());
  const onChangeTheme = (m) => { setThemeMode(m); setTheme(m); };
  // Numbers & Lines admin state
  const [numbers, setNumbers] = useState([]); // [{id?, phone_number, friendly_name, is_default, capabilities, carrier}]
  const [numbersLoading, setNumbersLoading] = useState(false);
  const [smsRequests, setSmsRequests] = useState([]); // [{id,e164,status,use_case,sample_messages,expected_volume,opt_in_note,admin_notes}]
  const [smsReqFor, setSmsReqFor] = useState(null);   // e164 with open SMS modal
  const [smsForm, setSmsForm] = useState({
    // quick picks (friendly)
    use_case:'reception', sample_messages:'', volume:'low', optInNote:'',
    // company
    company_name:'', company_address:'', company_url:'',
    contact_name:'', contact_email:'', contact_phone:'',
    entity_type:'', business_reg_number:'', reg_country:'',
    fortune500:'',
    // messaging
    use_case_text:'', advertises_product:'', previous_provider:'',
    advertised_where:'N/A', throughput:'3 MPS (Default)', sms_mms:'SMS',
    canada_primary:'No',
    // compliance
    opt_in_template:'', opt_out_process:'', help_template:'',
    terms:'', privacy_policy_url:'', additional_cta:'',
  });
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [searchArea, setSearchArea] = useState('');
  const [numbersSearched, setNumbersSearched] = useState(false);
  const [searchResults, setSearchResults] = useState([]); // available numbers from search
  const [searchFallback, setSearchFallback] = useState(''); // note when area-code search falls back
  const [searching, setSearching] = useState(false);
  const [selectedAvailable, setSelectedAvailable] = useState('');
  const [requestLabel, setRequestLabel] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [numbersError, setNumbersError] = useState('');
  const [editingNumberFor, setEditingNumberFor] = useState(null); // row key (id or phone) being relabeled
  const [editingLabel, setEditingLabel] = useState('');
  const [editBusy, setEditBusy] = useState('');
  const [defaultBusy, setDefaultBusy] = useState('');
  const [deleteBusy, setDeleteBusy] = useState('');
  // Desk phone (physical IP phone, e.g. Yealink T31W) — /api/desk-phone
  const [deskPhone, setDeskPhone] = useState(null); // {configured,server,username,caller_line,instructions,password?}
  const [deskPhoneLoading, setDeskPhoneLoading] = useState(false);
  const [deskPhoneBusy, setDeskPhoneBusy] = useState(false);
  const [deskPhoneError, setDeskPhoneError] = useState('');
  const [deskPhoneCopied, setDeskPhoneCopied] = useState('');
  const [openHours, setOpenHours] = useState(false);
  const [openServices, setOpenServices] = useState(false);
  const [openFaqs, setOpenFaqs] = useState(false);
    const [openClosedMsg, setOpenClosedMsg] = useState(false);
    const [openKnowledge, setOpenKnowledge] = useState(false);
  const [openVoiceAgent, setOpenVoiceAgent] = useState(false);
  const [openGreetingAgent, setOpenGreetingAgent] = useState(false);
  const [openTransferAgent, setOpenTransferAgent] = useState(false);
  const [openAlerts, setOpenAlerts] = useState(false);
  const [alertEmails, setAlertEmails] = useState([]);
  const [alertEvents, setAlertEvents] = useState({ missed_call:true, voicemail:true, failed_transfer:true, chatbot_lead:true });
  const [openVoicemail, setOpenVoicemail] = useState(false);
  const [subTabMoreOpen, setSubTabMoreOpen] = useState(false);
  // Where the overflow menu sits, measured when it opens: it is position:fixed so a short
  // window cannot clip it, and it scrolls when there are more tabs than room.
  const [subTabMoreBox, setSubTabMoreBox] = useState({ top: 0, right: 8, maxHeight: 240 });
  const panelRef = useRef(null);
  const subTabMoreRef = useRef(null);

  // Keep the sub-tab bar visually fixed: only the panel scrolls, and tab
  // switches always start at the top of that panel.
  useEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [tab]);

  // Close the sub-tab "More" dropdown on outside click/tap — same pattern
  // as the bottom nav's More menu in App.js.
  useEffect(() => {
    if (!subTabMoreOpen) return undefined;
    const onDown = (e) => {
      if (subTabMoreRef.current && !subTabMoreRef.current.contains(e.target)) {
        setSubTabMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [subTabMoreOpen]);

  const load = useCallback(async () => {
    if (!authToken) return;
    try {
      const q = selectedNumber ? `?number=${encodeURIComponent(selectedNumber)}` : '';
      const r = await fetch(API_BASE + '/api/agent/inbound-mode' + q, { headers: { Authorization: 'Bearer ' + authToken } });
      const d = await r.json();
      setMode(d.inbound_mode === 'human' ? 'human' : 'ai');
    } catch {}
  }, [authToken, selectedNumber]);

  useEffect(() => { load(); }, [load]);

  // Reload EVERY Settings field when the header Line changes (208 ≠ 520).
  useEffect(() => {
    setTranslate(loadTranslateSettings(selectedNumber));
    if (!authToken || !selectedNumber) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/agent/line-settings?number=${encodeURIComponent(selectedNumber)}`,
          { headers: { Authorization: 'Bearer ' + authToken } },
        );
        if (!r.ok || cancelled) return;
        const d = await r.json();
        applyLineTranslateFromServer(selectedNumber, d);
        if (cancelled) return;
        setTranslate(loadTranslateSettings(selectedNumber));
        if (d.inbound_mode === 'human' || d.inbound_mode === 'ai') setMode(d.inbound_mode);
        if (d.off_mode === 'ring' || d.off_mode === 'forward') setOffMode(d.off_mode);
        if (typeof d.voicemail_greeting === 'string') setVoicemailGreeting(d.voicemail_greeting);
        if (typeof d.greeting === 'string') setGreeting(d.greeting);
        if (typeof d.transfer_number === 'string') setTransferNumber(d.transfer_number);
        if (typeof d.owner_cell === 'string') setOwnerCell(d.owner_cell);
        if (Array.isArray(d.alert_emails)) setAlertEmails(d.alert_emails);
        if (d.alert_events && typeof d.alert_events === 'object') {
          setAlertEvents(prev => ({ ...prev, ...d.alert_events }));
        }
                if (typeof d.transfer_message === 'string') setTransferMessage(d.transfer_message);
        if (d.transfer_message_mode === 'custom' || d.transfer_message_mode === 'summary') setTransferMessageMode(d.transfer_message_mode);
                if (d.transfer_triggers && typeof d.transfer_triggers === 'object') setTransferTriggers(d.transfer_triggers);
        if (typeof d.after_hours_message === 'string') setAfterHoursMessage(d.after_hours_message);
        if (Array.isArray(d.services)) setServices(d.services);
        if (Array.isArray(d.faqs)) setFaqs(d.faqs);
        if (typeof d.knowledge_base === 'string') { setKnowledgeBase(d.knowledge_base); setKnowledgeDirty(false); }
        if (d.business_hours && typeof d.business_hours === 'object') {
          setBusinessHours(d.business_hours);
          setEnforceHours(!!d.enforce_business_hours);
          setHoursMode(detectHoursPreset(d.business_hours, !!d.enforce_business_hours));
        }
        if (d.voice_preference || d.agent_name) {
          setVoicePreference(d.voice_preference || d.agent_name);
        }
        if (typeof d.chatbot_enabled === 'boolean') setChatbotEnabled(d.chatbot_enabled);
        if (typeof d.chatbot_prompt === 'string') setChatbotPrompt(d.chatbot_prompt);
        // Always apply — missing key must clear sticky state from the prior line.
        setSchedulingEnabled(typeof d.scheduling_enabled === 'boolean' ? d.scheduling_enabled : false);
        if (d.rosie_lang_mode === 'en' || d.rosie_lang_mode === 'en_es' || d.rosie_lang_mode === 'en_es_auto') setRosieLangMode(d.rosie_lang_mode);
        if (d.recording_mode === 'off' || d.recording_mode === 'on_demand' || d.recording_mode === 'always') setRecordingMode(d.recording_mode);
        else if (typeof d.record_calls === 'boolean') setRecordingMode(d.record_calls ? 'always' : 'off');
        if (typeof d.record_consent_announce === 'boolean') setRecordConsentAnnounce(d.record_consent_announce);
        if (typeof d.keep_audio === 'boolean') setKeepAudio(d.keep_audio);
        setLoading(false);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [authToken, selectedNumber]);

  const loadConfig = useCallback(async () => {
    if (!authToken) return;
    // When a Line is selected, /api/agent/line-settings owns the form fields.
    if (selectedNumber) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(API_BASE + '/api/agent/config', { headers: { Authorization: 'Bearer ' + authToken } });
      const d = await r.json();
      const allLocs = d.locations || [{ id: 'primary' }];
      setLocations(allLocs);
      setMaxLocations(d.max_locations || 1);
      if (!activeLocId) setActiveLocId(allLocs[0].id);
      const loc = allLocs.find(l => l.id === activeLocId) || allLocs[0];
      setGreeting(loc.greeting || '');
      setTransferNumber(loc.transfer_number || '');
      setServices(loc.services || []);
      setFaqs(loc.faqs || []);
      setAfterHoursMessage(loc.after_hours_message || '');
      setVoicemailGreeting(loc.voicemail_greeting || '');
      if (loc.transfer_triggers) setTransferTriggers(loc.transfer_triggers);
      const bh = loc.business_hours || DEFAULT_HOURS_247();
      setBusinessHours(bh);
      setEnforceHours(!!loc.enforce_business_hours);
      setHoursMode(detectHoursPreset(bh, !!loc.enforce_business_hours));
    } catch(e) { /* /api/agent/config may be unavailable; voice loads separately */ }
    setLoading(false);
  }, [authToken, activeLocId, selectedNumber]);

  const loadVoice = useCallback(async () => {
    if (!authToken) return;
    try {
      const catalogRes = await fetch(API_BASE + '/api/agent/voices', { headers: { Authorization: 'Bearer ' + authToken } });
      if (catalogRes.ok) {
        const catalog = await catalogRes.json();
        setVoiceOptions(Array.isArray(catalog.voices) ? catalog.voices : []);
      }
      // Voice preference itself comes from line-settings when a Line is selected.
      if (selectedNumber) return;
      const prefRes = await fetch(API_BASE + '/api/agent/voice', { headers: { Authorization: 'Bearer ' + authToken } });
      if (prefRes.ok) {
        const pref = await prefRes.json();
        setVoicePreference(pref.voice_preference || pref.agent_name || 'rosie');
      }
    } catch { /* keep defaults */ }
  }, [authToken, selectedNumber]);

  const loadChatbot = useCallback(async () => {
    if (!authToken || selectedNumber) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/chatbot-config', { headers: { Authorization: 'Bearer ' + authToken } });
      const d = await r.json();
      setChatbotEnabled(!!d.chatbot_enabled);
      setChatbotPrompt(d.chatbot_prompt || '');
    } catch { setChatbotEnabled(false); }
  }, [authToken, selectedNumber]);

  useEffect(() => { loadConfig(); loadVoice(); loadChatbot(); }, [loadConfig, loadVoice, loadChatbot]);

  const loadMembers = useCallback(async () => {
    // Gate on phoneCount, not maxLocations — that's what actually decides
    // whether the Team tab is shown at all (see the tab list below). These
    // are different concepts (business locations vs. phone number count);
    // gating on maxLocations left this permanently unfetched for tenants
    // like Tumblerama that have >1 phone number but a single location.
    // Team is admin-only (agents never see the tab).
    if (!authToken || !showTeamTab) return;
    try {
      const r = await fetch(API_BASE + '/api/team/members', { headers: { Authorization: 'Bearer ' + authToken } });
      const d = await r.json();
      setMembers(d.members || []);
      setSeatInfo({ used: d.used || 0, included: d.included || 0, max: d.max || 0, remaining: d.remaining || 0 });
    } catch {}
  }, [authToken, showTeamTab]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const loadScheduling = useCallback(async () => {
    if (!authToken || !showSchedulingTab) return;
    try {
      const [rRes, rSvc] = await Promise.all([
        fetch(API_BASE + '/api/agent/scheduling/resources', { headers: { Authorization: 'Bearer ' + authToken } }),
        fetch(API_BASE + '/api/agent/scheduling/services', { headers: { Authorization: 'Bearer ' + authToken } }),
      ]);
      const dRes = await rRes.json();
      const dSvc = await rSvc.json();
      setResources(dRes.resources || []);
      setSchedServices(dSvc.services || []);
    } catch {}
  }, [authToken, showSchedulingTab]);

  useEffect(() => { loadScheduling(); }, [loadScheduling]);

  // Google Calendar (one-way) connection status for the Scheduling tab.
  const loadGcal = useCallback(async () => {
    if (!authToken || !showSchedulingTab) return;
    try {
      const r = await fetch(API_BASE + '/api/scheduling/google-calendar/status', {
        headers: { Authorization: 'Bearer ' + authToken },
      });
      if (!r.ok) return;
      setGcal(await r.json());
    } catch {}
  }, [authToken, showSchedulingTab]);

  useEffect(() => { loadGcal(); }, [loadGcal]);

  const handleGcalConnect = async () => {
    setGcalBusy(true);
    try {
      const r = await fetch(API_BASE + '/api/scheduling/google-calendar/connect', {
        headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) { setError(d.message || d.error || 'Could not start Google sign-in'); return; }
      window.location.href = d.url;
    } catch { setError('Could not start Google sign-in'); }
    finally { setGcalBusy(false); }
  };

  const handleGcalDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Existing events stay in Google; new bookings will not be added.')) return;
    setGcalBusy(true);
    try {
      const r = await fetch(API_BASE + '/api/scheduling/google-calendar/disconnect', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + authToken },
      });
      if (!r.ok) { setError('Could not disconnect Google Calendar'); return; }
      setGcal(g => ({ ...(g || {}), connected: false, email: '', calendar_id: 'primary' }));
    } catch { setError('Could not disconnect Google Calendar'); }
    finally { setGcalBusy(false); }
  };

  // Coming back from Google's consent screen: /?gcal=connected|error&reason=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('gcal');
    if (!result) return;
    if (isAdmin || isManager) setTab('scheduling');
    if (result === 'connected') {
      setSaveStatus('Google Calendar connected');
      setTimeout(() => setSaveStatus(''), 2500);
    } else {
      setError('Google Calendar connection failed (' + (params.get('reason') || 'unknown') + ')');
    }
  }, [isAdmin, isManager]);

  // Attendant roster. The API always answers with the whole week, so the grid
  // is rebuilt from `cells` rather than patched cell-by-cell.
  const applyRosterPayload = (d) => {
    setRoster(d);
    const draft = {};
    (d.slots || []).forEach(s => (d.days || []).forEach(day => {
      const cell = (d.cells || []).find(c => c.date === day.date && c.slot_start === s.start && c.slot_end === s.end);
      draft[`${day.date}|${s.start}`] = cell ? String(cell.resource_id) : '';
    }));
    setRosterDraft(draft);
  };

  const loadSlots = useCallback(async () => {
    if (!authToken || !showSchedulingTab) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/slots', {
        headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !Array.isArray(d.slots)) return;
      setSlotDraft(d.slots.map(s => ({ label: s.label || '', start: s.start, end: s.end })));
      setSlotDraftLoaded(true);
    } catch {}
  }, [authToken, showSchedulingTab]);

  // The slots PUT answers with the saved template; reload the roster after so
  // the grid rows match the new template.
  const saveSlots = async (slots) => {
    setSlotsSaving(true);
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/slots', {
        method: 'PUT',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots: slots.map(s => ({ label: slotLabel(s.start, s.end), start: s.start, end: s.end })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Could not save slots'); return false; }
      setSlotDraft((d.slots || []).map(s => ({ label: s.label || '', start: s.start, end: s.end })));
      await loadRoster(rosterWeekStart);
      setSaveStatus('Slots saved'); setTimeout(() => setSaveStatus(''), 2000);
      return true;
    } catch { setError('Could not save slots'); return false; }
    finally { setSlotsSaving(false); }
  };

  const handleAddSlot = async () => {
    if (!newSlot.start || !newSlot.end) { setError('Pick a start and end time for the new slot'); return; }
    const ok = await saveSlots([...slotDraft, { start: newSlot.start, end: newSlot.end }]);
    if (ok) setNewSlot({ label: '', start: '', end: '' });
  };

  const handleDeleteSlot = async (idx) => {
    await saveSlots(slotDraft.filter((_, i) => i !== idx));
  };

  const loadRoster = useCallback(async (weekStart) => {
    if (!authToken || !showSchedulingTab) return;
    try {
      const ws = fmtDateLocal(mondayOfWeek(weekStart || rosterWeekStart));
      const r = await fetch(API_BASE + '/api/agent/scheduling/roster?week_start=' + ws, {
        headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !Array.isArray(d.slots)) return;
      applyRosterPayload(d);
    } catch {}
  }, [authToken, showSchedulingTab, rosterWeekStart]);

  useEffect(() => {
    if (tab === 'scheduling') { loadSlots(); loadRoster(rosterWeekStart); }
  }, [tab, loadSlots, loadRoster, rosterWeekStart]);

  const handleSaveRoster = async () => {
    if (!roster) return;
    const assignments = [];
    (roster.days || []).forEach(day => (roster.slots || []).forEach(s => {
      const v = rosterDraft[`${day.date}|${s.start}`];
      assignments.push({ date: day.date, slot_start: s.start, slot_end: s.end, resource_id: v ? Number(v) : null });
    }));
    setRosterSaving(true);
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/roster', {
        method: 'PUT',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ week_start: roster.week_start, assignments }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Could not save the roster'); return; }
      applyRosterPayload(d);
      setSaveStatus('Roster saved'); setTimeout(() => setSaveStatus(''), 2000);
    } catch { setError('Could not save the roster'); }
    finally { setRosterSaving(false); }
  };

  // --- Copy week -----------------------------------------------------------
  // Target weeks are always Mondays; the server normalises them again in the
  // tenant timezone, so sending a local Monday here is safe.
  const rosterCopyTargets = (() => {
    if (!rosterCopyStart) return [];
    const start = mondayOfWeek(new Date(rosterCopyStart + 'T00:00:00'));
    const n = Math.max(1, Math.min(12, Number(rosterCopyWeeks) || 1));
    const weeks = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i * 7);
      weeks.push(fmtDateLocal(d));
    }
    return weeks;
  })();

  const fmtWeekRange = (weekStart) => {
    const start = new Date(weekStart + 'T00:00:00');
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const opts = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString(undefined, opts)} - ${end.toLocaleDateString(undefined, opts)}`;
  };

  const openRosterCopy = () => {
    const next = mondayOfWeek(rosterWeekStart);
    next.setDate(next.getDate() + 7);
    setRosterCopyStart(fmtDateLocal(next));
    setRosterCopyWeeks(1);
    setRosterCopyOverwrite(false);
    setRosterCopyResult(null);
    setRosterCopyOpen(true);
  };

  const handleCopyRoster = async () => {
    if (!roster || !authToken) return;
    if (!rosterCopyTargets.length) { setError('Pick the first week to copy to'); return; }
    if (rosterCopyTargets.includes(roster.week_start)) {
      setError('The target weeks include the week you are copying from');
      return;
    }
    setRosterCopying(true);
    setError('');
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/roster/copy', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_week_start: roster.week_start,
          target_week_starts: rosterCopyTargets,
          overwrite: rosterCopyOverwrite,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Could not copy the roster'); return; }
      setRosterCopyResult({
        copied: d.copied || [],
        skipped: d.skipped_non_empty || [],
        inactive: d.skipped_inactive || 0,
      });
      await loadRoster(rosterWeekStart);
    } catch { setError('Could not copy the roster'); }
    finally { setRosterCopying(false); }
  };

  const loadAppointments = useCallback(async () => {
    if (!authToken || !showSchedulingTab) return;
    setApptLoading(true);
    try {
      const start = apptWeekStart;
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      // Local calendar dates — toISOString() is UTC and shifts the week near midnight.
      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const qs = new URLSearchParams({ start: fmt(start), end: fmt(end) });
      if (apptResourceFilter) qs.set('resource_id', String(apptResourceFilter));
      const r = await fetch(API_BASE + '/api/agent/scheduling/appointments?' + qs.toString(), {
        headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json();
      setAppointments(d.appointments || []);
    } catch {
      setAppointments([]);
    } finally {
      setApptLoading(false);
    }
  }, [authToken, showSchedulingTab, apptWeekStart, apptResourceFilter]);

  useEffect(() => {
    if (tab === 'appointments') loadAppointments();
  }, [tab, loadAppointments]);

  const handleCancelAppointment = async (id, label) => {
    if (!window.confirm(`Cancel appointment${label ? ` for ${label}` : ''}?`)) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/appointments/' + id, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Could not cancel'); return; }
      loadAppointments();
    } catch { setError('Could not cancel'); }
  };

  const handleAddResource = async () => {
    const name = newResourceName.trim();
    if (!name) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/resources', {
        method: 'POST', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Could not add'); return; }
      setNewResourceName('');
      loadScheduling();
    } catch { setError('Could not add'); }
  };

  const handleRenameResource = async (id, name) => {
    try {
      await fetch(API_BASE + '/api/agent/scheduling/resources/' + id, {
        method: 'PATCH', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      loadScheduling();
    } catch { setError('Rename failed'); }
  };

  const handleDeleteResource = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This also removes its hours and cancels its scheduling history.`)) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/resources/' + id, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Delete failed'); return; }
      loadScheduling();
    } catch { setError('Delete failed'); }
  };

  const handleResourceHoursDayChange = (resourceId, day, field, value) => {
    setResources(prev => prev.map(res => res.id !== resourceId ? res : {
      ...res, hours: { ...res.hours, [day]: { ...res.hours[day], [field]: value } },
    }));
  };

  const handleSaveResourceHours = async (resourceId) => {
    const res = resources.find(r => r.id === resourceId);
    if (!res) return;
    try {
      const r = await fetch(API_BASE + `/api/agent/scheduling/resources/${resourceId}/hours`, {
        method: 'PUT', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours: res.hours }),
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); setError(d.error || 'Save failed'); return; }
      setSaveStatus('Saved'); setTimeout(() => setSaveStatus(''), 2000);
    } catch { setError('Save failed'); }
  };

  const handleAddSchedService = async () => {
    if (!newSchedService.name.trim()) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/services', {
        method: 'POST', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(newSchedService),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Could not add'); return; }
      setNewSchedService({ name: '', duration_minutes: 30, resource_ids: [] });
      loadScheduling();
    } catch { setError('Could not add'); }
  };

  const handleDeleteSchedService = async (id, name) => {
    if (!window.confirm(`Delete service "${name}"?`)) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/scheduling/services/' + id, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Delete failed'); return; }
      loadScheduling();
    } catch { setError('Delete failed'); }
  };

  const toggleNewServiceResource = (resourceId) => {
    setNewSchedService(prev => ({
      ...prev,
      resource_ids: prev.resource_ids.includes(resourceId)
        ? prev.resource_ids.filter(id => id !== resourceId)
        : [...prev.resource_ids, resourceId],
    }));
  };

  const handleResourceNameLocalChange = (id, name) => {
    setResources(prev => prev.map(r => r.id === id ? { ...r, name } : r));
  };

  // Agents and managers cannot sit on the Team tab; agents also cannot sit on
  // Scheduling/Appointments. (Numbers is allowed — it renders read-only for
  // non-admins.)
  useEffect(() => {
    const bounced = tab === 'team'
      ? !isAdmin
      : ((tab === 'scheduling' || tab === 'appointments') && !(isAdmin || isManager));
    if (bounced) setTab('business');
  }, [isAdmin, isManager, tab]);

  const switchLocation = (id) => {
    setActiveLocId(id);
    const loc = locations.find(l => l.id === id);
    if (loc) {
      setGreeting(loc.greeting || '');
      setTransferNumber(loc.transfer_number || '');
      setServices(loc.services || []);
      setFaqs(loc.faqs || []);
      setAfterHoursMessage(loc.after_hours_message || '');
      setVoicemailGreeting(loc.voicemail_greeting || '');
      if (loc.transfer_triggers) setTransferTriggers(loc.transfer_triggers);
      const bh = loc.business_hours || DEFAULT_HOURS_247();
      setBusinessHours(bh);
      setEnforceHours(!!loc.enforce_business_hours);
      setHoursMode(detectHoursPreset(bh, !!loc.enforce_business_hours));
    }
  };

  const handleAddLocation = async () => {
    if (!newLocName.trim()) return;
    try {
      const r = await fetch(API_BASE + '/api/agent/locations', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newLocName.trim(), copy_from_location_id: activeLocId }),
      });
      const d = await r.json();
      if (r.ok && d.location) {
        setLocations([...locations, d.location]);
        setNewLocName('');
        setActiveLocId(d.location.id);
        switchLocation(d.location.id);
      }
    } catch {}
  };

  const openNumbersEditor = async (memberId) => {
    setNumbersEditingFor(memberId);
    setNumberOptions([]);
    setSelectedNumberIds([]);
    try {
      const r = await fetch(API_BASE + '/api/team/members/' + memberId + '/numbers', {
        headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json();
      if (r.ok) {
        setNumberOptions(d.numbers || []);
        setSelectedNumberIds(d.assigned_ids || []);
      } else {
        setError(d.error || 'Failed to load numbers');
        setNumbersEditingFor(null);
      }
    } catch {
      setError('Failed to load numbers');
      setNumbersEditingFor(null);
    }
  };
  const toggleNumberSelection = (id) => {
    setSelectedNumberIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const saveNumbersEditor = async () => {
    if (!numbersEditingFor) return;
    setNumbersBusy(true);
    try {
      const r = await fetch(API_BASE + '/api/team/members/' + numbersEditingFor + '/numbers', {
        method: 'PUT', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ numbers: selectedNumberIds }),
      });
      if (r.ok) {
        setNumbersEditingFor(null);
        loadMembers();
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        const d = await r.json();
        setError(d.error || 'Failed to save numbers');
      }
    } catch {
      setError('Failed to save numbers');
    }
    setNumbersBusy(false);
  };

  const changeMemberRole = async (memberId, newRole) => {
    setOpenMemberMenu(null);
    try {
      const r = await fetch(API_BASE + '/api/team/members/' + memberId, {
        method: 'PATCH', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (r.ok) {
        loadMembers();
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        const d = await r.json();
        setError(d.error || 'Failed to change role');
      }
    } catch {
      setError('Failed to change role');
    }
  };

  const saveLinePatch = async (patch) => {
    if (!canEdit) {
      setError('Only admins can change line settings.');
      return false;
    }
    if (!selectedNumber) {
      setError('Select a Line in the header to save settings');
      return false;
    }
    try {
      const r = await fetch(API_BASE + '/api/agent/line-settings', {
        method: 'PUT',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ number: selectedNumber, ...patch }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || d.error || ('Save failed (' + r.status + ')'));
      }
      setSaveStatus('Saved');
      setTimeout(() => setSaveStatus(''), 2000);
      return true;
    } catch (e) {
      console.error('[SETTINGS] saveLinePatch error', e.message);
      setError(e.message || 'Save failed');
      return false;
    }
  };

  // Alerts: recipients are validated in the UI (fast feedback) AND on the
  // backend (400 on a bad address) so a typo can never silently drop a
  // recipient. An empty list means "use the account admin email".
  const saveAlertEmails = async (list) => {
    const cleaned = [];
    for (const raw of (list || [])) {
      const addr = String(raw || '').trim().toLowerCase();
      if (!addr) continue;
      if (!ALERT_EMAIL_RE.test(addr)) {
        setError(`Not a valid email address: ${addr}`);
        return false;
      }
      if (!cleaned.includes(addr)) cleaned.push(addr);
    }
    setError('');
    return saveConfig({ location: { alert_emails: cleaned } });
  };

  const saveAlertEvent = async (key, enabled) => {
    const next = { ...alertEvents, [key]: !!enabled };
    setAlertEvents(next);
    return saveConfig({ location: { alert_events: next } });
  };

  // All Profile/Agent fields save per selected Line (did_settings), not org-wide.
  const saveConfig = async (data) => {
    const loc = (data && data.location) || {};
    const patch = { ...loc };
    if ('enforce_business_hours' in loc) patch.enforce_business_hours = !!loc.enforce_business_hours;
    if (selectedNumber) return saveLinePatch(patch);
    setError('Select a Line in the header to save settings');
    return false;
  };

  // Agent handlers
  const handleAddService = async () => {
    const s = [...services, { ...newService }];
    setServices(s); setNewService({ name:'', description:'', price:'' });
    await saveConfig({ location: { services: s } });
  };
  const handleRemoveService = async (i) => {
    const s = services.filter((_,j) => j!==i);
    setServices(s); await saveConfig({ location: { services: s } });
  };
  const handleAddFaq = async () => {
    const f = [...faqs, { ...newFaq }];
    setFaqs(f); setNewFaq({ question:'', answer:'' });
    await saveConfig({ location: { faqs: f } });
  };
  const handleRemoveFaq = async (i) => {
    const f = faqs.filter((_,j) => j!==i);
    setFaqs(f); await saveConfig({ location: { faqs: f } });
  };
  const handleSaveVoice = async () => {
    await saveLinePatch({ voice_preference: voicePreference });
  };
  const handleSaveClosedMessage = async () => {
    await saveConfig({ location: { after_hours_message: afterHoursMessage } });
  };

  const applyHoursPreset = async (preset) => {
    setHoursMode(preset);
    if (preset === 'custom') return; // reveal the editor, save happens via handleSaveHours
    const bh = preset === '24/7' ? DEFAULT_HOURS_247() : DEFAULT_HOURS_MONFRI();
    const enforce = preset !== '24/7';
    setBusinessHours(bh);
    setEnforceHours(enforce);
    await saveConfig({ location: { business_hours: bh, enforce_business_hours: enforce } });
  };
  const handleHoursDayChange = (day, field, value) => {
    setBusinessHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };
  const handleSaveHours = async () => {
    await saveConfig({ location: { business_hours: businessHours, enforce_business_hours: true } });
    setEnforceHours(true);
  };

  const voicesByGender = (gender) => voiceOptions.filter((v) => v.gender === gender);
  const handleHearGreeting = async () => {
    setHearing(true); setAudioSrc(null);
    try {
      const r = await fetch(API_BASE + '/api/agent/config/preview-greeting', {
        method:'POST', headers:{ Authorization:'Bearer '+authToken, 'Content-Type':'application/json' },
        body:JSON.stringify({ text:greeting||'Hello, thanks for calling.', voice: voicePreference }),
      });
      if (r.ok) { const b=await r.blob(); setAudioSrc(URL.createObjectURL(b)); }
    } catch(e) { setError('Hear it failed'); }
    setHearing(false);
  };

  // Translate helper — entire pair + persona + SMS mode are per Line.
  const updateTranslate = (patch) => {
    if (!canEdit) {
      setError('Only admins can change translate settings.');
      return;
    }
    const t = { ...translate, ...patch };
    setTranslate(t); setTranslateSaved(false);
    saveTranslateSettings(t, selectedNumber); setTranslateSaved(true);
    if (authToken && selectedNumber) {
      const body = {
        number: selectedNumber,
        user_lang: t.userLang,
        remote_lang: t.remoteLang,
        domain: t.domain,
        auto_detect: t.autoDetect !== false,
        sms_mode: t.smsMode,
      };
      fetch(API_BASE + '/api/agent/line-settings', {
        method: 'PUT',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => {});
    }
  };

  // SMS Translate mode is a per-line comms setting any user with access to
  // the line can change (unlike the rest of this tab, which is admin-only)
  // — sent as its own PUT with only sms_mode in the body so the backend can
  // allow it without opening up the admin-gated fields alongside it.
  const updateSmsMode = (mode) => {
    const t = { ...translate, smsMode: mode };
    setTranslate(t); setTranslateSaved(false);
    saveTranslateSettings(t, selectedNumber); setTranslateSaved(true);
    if (authToken && selectedNumber) {
      fetch(API_BASE + '/api/agent/line-settings', {
        method: 'PUT',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ number: selectedNumber, sms_mode: mode }),
      }).catch(() => {});
    }
  };

  const lineLabel = (() => {
    const d = String(selectedNumber || '').replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('1')) {
      return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
    }
    if (d.length === 10) {
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    return selectedNumber || 'No line selected';
  })();

  // ---------- Numbers & Lines (admin self-service) ----------
  const loadNumbers = useCallback(async () => {
    if (!authToken) return;
    setNumbersLoading(true);
    try {
      const r = await fetch(API_BASE + '/numbers', { headers: { Authorization: 'Bearer ' + authToken } });
      if (!r.ok) { setNumbers([]); return; }
      const d = await r.json();
      setNumbers(Array.isArray(d) ? d : (d.numbers || []));
    } catch { setNumbers([]); }
    finally { setNumbersLoading(false); }
  }, [authToken]);

  useEffect(() => {
    // Both admins and agents need the numbers list — admins to manage them,
    // agents to see which number is assigned to them (read-only).
    loadNumbers();
  }, [loadNumbers, isAdmin]);

  // ---------- Desk phone (physical IP phone) ----------
  // Self-service for any signed-in user: one SIP login per user, separate
  // from the app's own endpoint so the phone rings alongside the app without
  // stealing its registration.
  const loadDeskPhone = useCallback(async () => {
    if (!authToken) return;
    setDeskPhoneLoading(true);
    setDeskPhoneError('');
    try {
      const r = await fetch(API_BASE + '/api/desk-phone', { headers: { Authorization: 'Bearer ' + authToken } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setDeskPhone(null); setDeskPhoneError(d.message || d.error || 'Could not load desk phone status'); return; }
      setDeskPhone(d);
    } catch { setDeskPhone(null); setDeskPhoneError('Could not load desk phone status'); }
    finally { setDeskPhoneLoading(false); }
  }, [authToken]);

  useEffect(() => { loadDeskPhone(); }, [loadDeskPhone]);

  const handleDeskPhoneProvision = async () => {
    setDeskPhoneBusy(true); setDeskPhoneError(''); setDeskPhoneCopied('');
    try {
      const r = await fetch(API_BASE + '/api/desk-phone/provision', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + authToken },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setDeskPhoneError(d.message || d.error || 'Could not set up the desk phone'); return; }
      // The password is shown exactly once — it is not recoverable later.
      setDeskPhone({ ...d, password: d.password || '' });
    } catch { setDeskPhoneError('Could not set up the desk phone'); }
    finally { setDeskPhoneBusy(false); }
  };

  const copyDeskPhoneField = async (key, value) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(value || '');
      setDeskPhoneCopied(key);
      setTimeout(() => setDeskPhoneCopied(''), 1500);
    } catch { setDeskPhoneError('Copy failed — select the value and copy it manually.'); }
  };

  const loadSmsRequests = useCallback(async () => {
    if (!authToken) return;
    try {
      const r = await fetch(API_BASE + '/api/numbers/sms-requests', { headers: { Authorization: 'Bearer ' + authToken } });
      if (!r.ok) { setSmsRequests([]); return; }
      const d = await r.json();
      setSmsRequests(Array.isArray(d) ? d : (d.requests || []));
    } catch { setSmsRequests([]); }
  }, [authToken]);

  useEffect(() => {
    if (isAdmin) { loadSmsRequests(); }
  }, [loadSmsRequests, isAdmin]);

  const TFV_NPAS = ['800','833','844','855','866','877','888'];
  const isTollFreeNum = (p) => { const d=(p||'').replace(/\D/g,''); return d.length===11 && d[0]==='1' && TFV_NPAS.includes(d.slice(1,4)); };
  const smsReqStatus = (e164) => { const d=(e164||'').replace(/\D/g,''); return smsRequests.find(r => (r.e164||'').replace(/\D/g,'')===d) || null; };

  // Rebuild the modal form from a stored request (form_data) so a pending
  // request can be edited in place (e.g. correct the EIN) and re-submitted.
  const smsFormFromReq = (req) => {
    const fd = (req && req.form_data) || {};
    const volRev = { '1,000':'low', '10,000':'medium', '100,000':'high' };
    const volume = (fd.volume_per_month && volRev[fd.volume_per_month]) || req.expected_volume || 'low';
    const use_case = ['reception','service','appointments','ordering','other'].includes(req && req.use_case) ? (req.use_case) : 'other';
    return {
      use_case,
      sample_messages: (req && req.sample_messages) || '',
      volume,
      optInNote: (req && req.opt_in_note) || '',
      company_name: fd.company_name || '', company_address: fd.company_address || '', company_url: fd.company_url || '',
      contact_name: fd.contact_name || '', contact_email: fd.contact_email || '', contact_phone: fd.contact_phone || '',
      entity_type: fd.entity_type || '', business_reg_number: fd.business_reg_number || '', reg_country: fd.reg_country || '',
      fortune500: fd.fortune500 || '',
      use_case_text: fd.use_case_text || '', advertises_product: fd.advertises_product || '', previous_provider: fd.previous_provider || '',
      advertised_where: fd.advertised_where || 'N/A', throughput: fd.throughput || '3 MPS (Default)', sms_mms: fd.sms_mms || 'SMS', canada_primary: fd.canada_primary || 'No',
      opt_in_template: fd.opt_in_template || '', opt_out_process: fd.opt_out_process || '',
      help_template: fd.help_template || '', terms: fd.terms || '', privacy_policy_url: fd.privacy_policy_url || '', additional_cta: fd.additional_cta || 'N/A',
    };
  };

  const handleSubmitSmsRequest = async () => {
    if (!smsReqFor || !smsForm.sample_messages.trim() || !smsForm.company_name.trim()) return;
    setSmsBusy(true); setSmsError('');
    try {
      const f = smsForm;
      const VOL_NUM = { low:'1,000', medium:'10,000', high:'100,000' };
      const body = {
        use_case: f.use_case,
        sample_messages: f.sample_messages,
        expected_volume: f.volume,
        opt_in_note: f.optInNote,
        // company
        company_name: f.company_name,
        company_address: f.company_address,
        company_url: f.company_url,
        contact_name: f.contact_name,
        contact_email: f.contact_email,
        contact_phone: f.contact_phone,
        entity_type: f.entity_type,
        business_reg_number: f.business_reg_number,
        reg_country: f.reg_country,
        fortune500: f.fortune500,
        // messaging
        use_case_text: f.use_case_text || f.use_case,
        advertises_product: f.advertises_product,
        previous_provider: f.previous_provider,
        advertised_where: f.advertised_where || 'N/A',
        volume_per_month: (VOL_NUM[f.volume] || f.volume),
        throughput: f.throughput,
        sms_mms: f.sms_mms,
        canada_primary: f.canada_primary,
        // compliance
        opt_in_template: f.opt_in_template || f.optInNote,
        opt_out_process: f.opt_out_process,
        help_template: f.help_template,
        terms: f.terms,
        privacy_policy_url: f.privacy_policy_url,
        additional_cta: f.additional_cta,
      };
      const r = await fetch(API_BASE + '/api/numbers/' + smsReqFor.replace(/[^0-9]/g,'') + '/request-sms', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(()=>({}));
      if (!r.ok) { setSmsError(d.message || d.error || 'Request failed'); return; }
      setSmsReqFor(null);
      loadSmsRequests();
    } catch { setSmsError('Request failed — try again.'); }
    finally { setSmsBusy(false); }
  };

  const handleSearchNumbers = async () => {
    const ac = searchArea.trim();
    if (!ac) { setNumbersError('Enter an area code to search.'); return; }
    setSearching(true); setNumbersError(''); setNumbersSearched(false); setSearchFallback('');
    try {
      let d, r = await fetch(API_BASE + '/api/numbers/search', {
        method: 'POST', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ area_code: ac }),
      });
      d = await r.json();
      if (!r.ok) { setNumbersError(d.error || d.message || 'Search failed'); setSearchResults([]); setNumbersSearched(true); return; }
      let results = Array.isArray(d) ? d : (d.numbers || []);
      let fallbackNote = '';
      if (results.length === 0) {
        // Option A: no numbers in that area code — fall back to whatever IS
        // available elsewhere, and tell the customer why.
        setNumbersSearched(true);
        const r2 = await fetch(API_BASE + '/api/numbers/search', {
          method: 'POST', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const d2 = await r2.json();
        if (r2.ok) {
          results = Array.isArray(d2) ? d2 : (d2.numbers || []);
          fallbackNote = 'None available in ' + ac + ' right now — here are available numbers elsewhere.';
        } else {
          fallbackNote = 'None available in ' + ac + ' right now. Try another area code.';
        }
      }
      setSearchFallback(fallbackNote);
      setSearchResults(results);
      setNumbersSearched(true);
    } catch { setNumbersError('Search failed'); setSearchResults([]); setNumbersSearched(true); }
    finally { setSearching(false); }
  };

  const handleRequestNumber = async () => {
    if (!selectedAvailable) { setNumbersError('Select an available number first.'); return; }
    setRequesting(true); setNumbersError('');
    try {
      const r = await fetch(API_BASE + '/api/numbers/request', {
        method: 'POST', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: selectedAvailable, label: requestLabel.trim() || selectedAvailable }),
      });
      const d = await r.json();
      if (!r.ok) {
        if (r.status === 402 && d.code === 'number_requires_paid_plan') {
          setNumbersError('A paid plan is required to request a phone number.');
          handleChoosePlan(); // send them to Hub to pick a plan
          return;
        }
        setNumbersError(d.error || d.message || 'Could not request the number'); return;
      }
      setSelectedAvailable(''); setRequestLabel(''); setSearchResults([]); setNumbersSearched(false);
      setSaveStatus('Number added'); setTimeout(() => setSaveStatus(''), 2000);
      loadNumbers();
    } catch { setNumbersError('Request failed'); }
    finally { setRequesting(false); }
  };

  const handleChoosePlan = async () => {
    // Plan changes are owned by Solvit Hub (identity/billing platform). Send
    // the user straight to Hub's dashboard billing form — it renders for every
    // subscriber regardless of which products they have.
    let url;
    try {
      url = new URL(HUB_UPGRADE_URL);
      url.pathname = '/dashboard';
      url.hash = '#billingForm';
    } catch {
      // HUB_UPGRADE_URL should always be absolute; fall back to the value verbatim.
      window.location.href = HUB_UPGRADE_URL;
      return;
    }
    const href = url.toString();
    if (isNativePlatform()) {
      // Native Capacitor build: a plain window.location redirect would steer the
      // app's own WebView away to Hub, stranding the user outside the app shell
      // with no way back but force-quitting. Open Hub in the system browser
      // (SFSafariViewController / Chrome Custom Tab) instead — same pattern as
      // Login.js's SSO flow.
      try {
        await Browser.open({ url: href });
      } catch (e) {
        console.warn('[Settings] Browser.open failed, falling back to <a>', e);
        // Last resort: open in the system browser via a real anchor.
        const a = document.createElement('a');
        a.href = href;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
      }
    } else {
      window.location.href = href;
    }
  };

  const handleSetDefault = async (n) => {
    const id = n.id || n.phone_number;
    if (!id) return;
    setDefaultBusy(id); setNumbersError('');
    try {
      const r = await fetch(API_BASE + '/api/numbers/' + encodeURIComponent(id), {
        method: 'PUT', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_default: true }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setNumbersError(d.error || d.message || 'Could not set default line'); return; }
      setSaveStatus('Default updated'); setTimeout(() => setSaveStatus(''), 2000);
      loadNumbers();
    } catch { setNumbersError('Could not set default line'); }
    finally { setDefaultBusy(''); }
  };

  const saveNumberEdit = async (n) => {
    const id = n.id || n.phone_number;
    if (!id) return;
    setEditBusy(id); setNumbersError('');
    try {
      const r = await fetch(API_BASE + '/api/numbers/' + encodeURIComponent(id), {
        method: 'PUT', headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: editingLabel.trim() || n.phone_number }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setNumbersError(d.error || d.message || 'Save failed'); return; }
      setEditingNumberFor(null); setEditingLabel('');
      setSaveStatus('Saved'); setTimeout(() => setSaveStatus(''), 2000);
      loadNumbers();
    } catch { setNumbersError('Save failed'); }
    finally { setEditBusy(''); }
  };

  const handleCloseNumber = async (n) => {
    const e164 = n.phone_number;
    if (!window.confirm(`Close number ${e164}? It will be removed from your account and this line will stop ringing.`)) return;
    setDeleteBusy(e164); setNumbersError('');
    try {
      const r = await fetch(API_BASE + '/api/numbers/' + encodeURIComponent(e164), {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + authToken },
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setNumbersError(d.error || d.message || 'Delete failed'); return; }
      setSaveStatus('Number closed'); setTimeout(() => setSaveStatus(''), 2000);
      loadNumbers();
    } catch { setNumbersError('Delete failed'); }
    finally { setDeleteBusy(''); }
  };

  if (loading) return <div style={{padding:20}}>Loading...</div>;
  const normPhone = (s) => String(s || '').replace(/[^0-9]/g, '');
  const CAP_BADGE = { fontSize:'0.75rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.02em',padding:'2px 7px',borderRadius:99,background:'var(--truva-info-tint)',color:'var(--truva-action-blue)',whiteSpace:'nowrap' };

  const IS = { width:'100%', padding:'10px 12px', boxSizing:'border-box', marginBottom:'8px', border:'var(--truva-hairline) solid var(--truva-separator)', borderRadius:'var(--truva-radius-sm)', background:'var(--truva-surface-2)', color:'var(--truva-text)', fontFamily:'inherit', fontSize:'var(--truva-type-body)', minHeight:'var(--truva-list-row-min-h)' };
  const BTN = { padding:'0 20px', minHeight:'var(--truva-list-row-min-h)', border:'none', backgroundColor:'var(--truva-action-blue)', color:'white', borderRadius:'var(--truva-radius-pill)', cursor:'pointer', fontFamily:'inherit', fontSize:'var(--truva-type-body)', fontWeight:'var(--truva-weight-semibold)' };
  // Padding lives on summary + body so collapsed/expanded height stays clean.
  const SS = { border:'none', margin:'0 var(--truva-list-inset) 12px', borderRadius:'var(--truva-radius-grouped)', background:'var(--truva-grouped-row-bg)', overflow:'hidden', color:'var(--truva-text)' };
  const SUMMARY = {
    cursor:'pointer', fontWeight:'var(--truva-weight-regular)', fontSize:'var(--truva-type-body)', color:'var(--truva-text)',
    padding:'0 var(--truva-list-inset)', minHeight:'var(--truva-list-row-min-h)', userSelect:'none', display:'flex', alignItems:'center',
    gap:10, listStyle:'none', margin:0, lineHeight:1.25, boxSizing:'border-box',
  };
  const DETAILS_BODY = { padding:'var(--truva-list-inset)' };
  const CHEVRON = (open) => ({
    display:'inline-flex', width:16, height:16, flexShrink:0, order:2, marginLeft:'auto',
    alignItems:'center', justifyContent:'center', fontSize:'0.8rem',
    color:'var(--truva-text-secondary)', lineHeight:1,
    transition:'transform 0.15s ease',
    transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
  });
  // Equal-width tabs, constant font-weight - only color/underline change on
  // select, so labels never shove neighbors when switching tabs.
  const TAB = (t) => ({
    flex:'1 1 0', minWidth:0, display:'flex', alignItems:'center', justifyContent:'center',
    height:44, padding:'0 8px', margin:0, boxSizing:'border-box',
    border:'none', borderRadius:0, borderBottom: tab===t ? '3px solid var(--truva-action-blue)' : '3px solid transparent',
    background:'transparent', fontFamily:'inherit', fontSize:'0.9rem',
    fontWeight:600, color: tab===t ? 'var(--truva-action-blue)' : 'var(--truva-text-secondary)',
    cursor:'pointer', whiteSpace:'nowrap', lineHeight:1,
    WebkitAppearance:'none', appearance:'none',
  });

  return (
    <div style={{
      flex:1, minHeight:0, width:'100%', maxWidth:760, margin:'0 auto',
      display:'flex', flexDirection:'column', overflow:'hidden',
      fontFamily:'Inter, sans-serif',
    }}>
      <style>{`
        .settings-details > summary { list-style: none; }
        .settings-details > summary::-webkit-details-marker { display: none; }
        .settings-details > summary::marker { content: ''; }
      `}</style>
      <div style={{
        flexShrink:0, display:'flex', flexDirection:'row', alignItems:'stretch', gap:0,
        height:44, borderBottom:'2px solid var(--truva-border)', background:'var(--truva-surface)', zIndex:10,
      }}>
        {(() => {
          const TAB_LABELS_MAP = {business:'Profile',agent:'Agent',chatbot:'Chatbot',translate:'Translate',numbers:'Numbers',scheduling:'Scheduling',appointments:'Appointments',team:'Team'};
          const allTabs = ['business','agent','chatbot','translate']
            .concat(['numbers'])
            .concat(showSchedulingTab ? ['scheduling', 'appointments'] : [])
            .concat(showTeamTab ? ['team'] : []);
          // Same idea as the bottom nav's More (⋯): a fixed set of primary
          // tabs stays visible, the rest collapse into a dropdown — only on
          // narrow layouts, where 5-6 flat tabs get cramped to the point of
          // being hard to tap. Full row on wide screens, unchanged.
          const PRIMARY_COUNT = 3;
          const primaryTabs = narrowLayout ? allTabs.slice(0, PRIMARY_COUNT) : allTabs;
          const moreTabs = narrowLayout ? allTabs.slice(PRIMARY_COUNT) : [];
          return (
            <>
              {primaryTabs.map(t => (
                <button key={t} type="button" onClick={()=>{setTab(t);setSubTabMoreOpen(false);}} style={TAB(t)}>
                  {TAB_LABELS_MAP[t]}
                </button>
              ))}
              {moreTabs.length > 0 && (
                <div ref={subTabMoreRef} style={{position:'relative',flex:'1 1 0',minWidth:0,display:'flex'}}>
                  <button type="button"
                    aria-haspopup="menu" aria-expanded={subTabMoreOpen} aria-label="More settings tabs"
                    onClick={(e)=>{
                      const r = e.currentTarget.getBoundingClientRect();
                      setSubTabMoreBox({
                        top: Math.round(r.bottom + 6),
                        right: Math.max(8, Math.round(window.innerWidth - r.right)),
                        maxHeight: Math.max(120, Math.round(window.innerHeight - r.bottom - 14)),
                      });
                      setSubTabMoreOpen(o=>!o);
                    }}
                    style={{...TAB(moreTabs.includes(tab) ? tab : '__none__'),width:'100%'}}>
                    &#8943;
                  </button>
                  {subTabMoreOpen && (
                    <div className="overflow-menu" role="menu" style={{position:'fixed',top:subTabMoreBox.top,right:subTabMoreBox.right,left:'auto',maxHeight:subTabMoreBox.maxHeight,overflowY:'auto'}}>
                      {moreTabs.map(t => (
                        <button key={t} type="button" role="menuitem"
                          className={`overflow-menu-item${tab===t?' active':''}`}
                          onClick={()=>{setTab(t);setSubTabMoreOpen(false);}}>
                          <span>{TAB_LABELS_MAP[t]}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </div>
      <div
        ref={panelRef}
        className="settings-panel"
        style={{
          flex:1, minHeight:0, overflowY:'auto', overflowAnchor:'none',
          WebkitOverflowScrolling:'touch', padding:'16px 12px 24px',
        }}
      >
      {user && user.is_free === true && (
        <div style={{
          marginBottom:12, padding:'12px 14px', borderRadius:8,
          background:'var(--truva-warn-tint-chip)', border:'1px solid var(--truva-warn-border)', fontSize:'0.95rem', color:'var(--truva-warn-text)',
        }}>
          <strong>Your account is on the free Greet plan.</strong> Choose a paid plan to get your
          own phone number, full calling minutes, and advanced features.
          <div style={{marginTop:8, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
            <button type="button" onClick={handleChoosePlan} style={BTN}>
              Choose a plan
            </button>
          </div>
        </div>
      )}
      {/* Appearance — System / Light / Dark theme override */}
      <div style={{
        marginBottom:12, padding:'12px 14px', borderRadius:8,
        background:'var(--truva-surface)', border:'1px solid var(--truva-border)',
      }}>
        <div style={{fontSize:'0.95rem', fontWeight:600, color:'var(--truva-text)', marginBottom:8}}>
          Appearance
        </div>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          {[['system','System'],['light','Light'],['dark','Dark']].map(([val,label]) => {
            const active = theme === val;
            return (
              <button key={val} type="button"
                aria-pressed={active}
                onClick={() => onChangeTheme(val)}
                style={{
                  flex:'1 1 0', minWidth:80, padding:'8px 10px', borderRadius:8,
                  cursor:'pointer',
                  background: active ? 'var(--truva-action-blue)' : 'var(--truva-surface-2)',
                  color: active ? 'var(--truva-on-action)' : 'var(--truva-text)',
                  border:'1px solid ' + (active ? 'var(--truva-action-blue)' : 'var(--truva-border)'),
                  fontSize:'0.95rem', fontWeight: active ? 600 : 400,
                }}
              >{label}</button>
            );
          })}
        </div>
        <div style={{marginTop:8, fontSize:'0.85rem', color:'var(--truva-text-secondary)'}}>
          {theme === 'system'
            ? 'Follow your device appearance automatically.'
            : 'Override the device appearance with this setting.'}
        </div>
      </div>

      {!canEdit && (
        <div style={{
          marginBottom:12, padding:'10px 12px', borderRadius:8,
          background:'var(--truva-surface)', border:'1px solid var(--truva-border)', fontSize:'0.95rem', color:'var(--truva-text-secondary)',
        }}>
          View only — ask an admin to change Profile, Agent, Chatbot, or Translate for this line.
        </div>
      )}
      {selectedNumber ? (
        <div style={{
          marginBottom:12, padding:'10px 12px', borderRadius:8,
          background:'var(--truva-action-blue-100)', border:'1px solid var(--truva-action-blue-200)', fontSize:'0.95rem', color:'var(--truva-text)',
        }}>
          Line settings for <strong>{lineLabel}</strong>
          <div style={{marginTop:4, fontSize:'0.85rem', color:'var(--truva-text-secondary)'}}>
            {canEdit
              ? 'Profile, Agent, Chatbot, and Translate all save for this line only. Team seats stay account-wide.'
              : 'Showing admin-configured settings for this line. Softphone still works on your assigned numbers.'}
          </div>
        </div>
      ) : (
        <div style={{
          marginBottom:12, padding:'10px 12px', borderRadius:8,
          background:'var(--truva-warn-tint-chip)', border:'1px solid var(--truva-warn-border)', fontSize:'0.95rem', color:'var(--truva-warn-text)',
        }}>
          Select a Line in the header to {canEdit ? 'edit' : 'view'} per-number settings.
        </div>
      )}
      {error && <div style={{color:'var(--truva-danger-text)',fontSize:'0.95rem',margin:'0 0 4px 0'}}>{error}</div>}
      {saveStatus && <div style={{position:'fixed',top:8,right:16,color:'var(--truva-on-action)',background:saveStatus.includes('failed')?'var(--truva-danger-red)':'var(--truva-success-green)',padding:'6px 16px',borderRadius:6,fontSize:'0.95rem',zIndex:100}}>{saveStatus}</div>}
      {/* Header Line is the settings scope; hide legacy location chips when a line is selected. */}
      {!selectedNumber && (
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12}}>
        {locations.map(loc => (
          <button key={loc.id} type="button" onClick={()=>switchLocation(loc.id)}
            style={{padding:'4px 12px',borderRadius:16,border:'1px solid '+(activeLocId===loc.id?'var(--truva-action-blue)':'var(--truva-border)'),background:activeLocId===loc.id?'var(--truva-action-blue)':'var(--truva-surface)',color:activeLocId===loc.id?'var(--truva-on-action)':'var(--truva-text)',fontSize:'0.9rem',cursor:'pointer',whiteSpace:'nowrap'}}>
            {loc.name || 'Main'}
          </button>
        ))}
        {maxLocations > 1 && locations.length < maxLocations && (
          <button type="button" onClick={()=>{const n=prompt('Location name:'); if(n&&n.trim()){setNewLocName(n.trim());handleAddLocation();}}}
            style={{padding:'4px 12px',borderRadius:16,border:'1px dashed var(--truva-action-blue)',background:'transparent',color:'var(--truva-action-blue)',fontSize:'0.9rem',cursor:'pointer'}}>
            + Add
          </button>
        )}
      </div>
      )}
      {/* === BUSINESS PROFILE TAB === */}
      {tab === 'business' && (
        <ErrorBoundary inline label="Profile">
        <>
        <section style={{...SS, padding:16}}>
          <h3 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Display timezone</h3>
          <p style={{margin:'0 0 10px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
            Recents, Messages, and Voicemail use this zone. It is yours, not the line&apos;s business hours.
          </p>
          <select
            style={IS}
            value={displayTz}
            onChange={(e) => setDisplayTz(saveDisplayTimeZone(e.target.value))}
            aria-label="Display timezone"
          >
            {timezoneChoices(displayTz).map((z) => (
              <option key={z.id} value={z.id}>{z.label}</option>
            ))}
          </select>
          <p style={{margin:0,fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>
            Now: {formatInTimeZone(new Date(), displayTz)}
          </p>
        </section>
        <CreditUsage authToken={authToken} canManageBilling={isAdmin} />
        <fieldset disabled={!canEdit} style={{border:'none',margin:0,padding:0,minWidth:0}}>
        <details className="settings-details" open={openHours} onToggle={e=>setOpenHours(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openHours)} aria-hidden="true">▼</span>Hours</summary>
          <div style={DETAILS_BODY}>
            <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom: hoursMode==='custom' ? 12 : 0}}>
              {[{key:'24/7',label:'24/7'},{key:'mon-fri',label:'Mon-Fri 9-5'},{key:'custom',label:'Custom'}].map(p=>(
                <button key={p.key} type="button"
                  onClick={()=>applyHoursPreset(p.key)}
                  style={{...BTN,backgroundColor:hoursMode===p.key?'var(--truva-action-blue)':'var(--truva-btn-secondary-bg)',fontSize:'0.9rem',padding:'6px 12px'}}>
                  {p.label}
                </button>
              ))}
            </div>
            {hoursMode==='custom' && (
              <>
                {HOURS_DAYS.map(day => {
                  const d = businessHours[day] || {};
                  return (
                    <div key={day} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0'}}>
                      <span style={{width:36,fontSize:'0.95rem',color:'var(--truva-text)'}}>{HOURS_DAY_LABELS[day]}</span>
                      <label style={{display:'flex',alignItems:'center',gap:4,fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>
                        <input type="checkbox" checked={!!d.closed}
                          onChange={e=>handleHoursDayChange(day,'closed',e.target.checked)} />
                        Closed
                      </label>
                      <input type="time" value={d.open || '09:00'} disabled={!!d.closed}
                        onChange={e=>handleHoursDayChange(day,'open',e.target.value)}
                        style={{padding:4,fontSize:'0.9rem',opacity:d.closed?0.5:1}} />
                      <span style={{fontSize:'0.9rem',color:'var(--truva-text-tertiary)'}}>to</span>
                      <input type="time" value={d.close || '17:00'} disabled={!!d.closed}
                        onChange={e=>handleHoursDayChange(day,'close',e.target.value)}
                        style={{padding:4,fontSize:'0.9rem',opacity:d.closed?0.5:1}} />
                    </div>
                  );
                })}
                <button type="button" style={{...BTN,marginTop:10}} onClick={handleSaveHours}>Save Hours</button>
              </>
            )}
          </div>
        </details>

        <details className="settings-details" open={openServices} onToggle={e=>setOpenServices(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openServices)} aria-hidden="true">▼</span>Services</summary>
          <div style={DETAILS_BODY}>
            <div style={{border:'1px solid var(--truva-border)',padding:10,borderRadius:6,marginBottom:10}}>
              <input style={IS} placeholder="Service name" value={newService.name} onChange={e=>setNewService({...newService,name:e.target.value})} />
              <input style={IS} placeholder="Description" value={newService.description} onChange={e=>setNewService({...newService,description:e.target.value})} />
              <input style={IS} placeholder="Price (e.g. $50)" value={newService.price} onChange={e=>setNewService({...newService,price:e.target.value})} />
              <button type="button" style={BTN} onClick={handleAddService}>Add Service</button>
            </div>
            {services.map((svc,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'start',padding:'8px 0',borderBottom:'1px solid var(--truva-border)'}}>
                <div><strong>{svc.name}</strong> {svc.price ? '('+svc.price+')' : ''}<br/><span style={{color:'var(--truva-text-secondary)',fontSize:'0.95rem'}}>{svc.description}</span></div>
                <button type="button" onClick={()=>handleRemoveService(i)} style={{...BTN,backgroundColor:'var(--truva-danger-red)',padding:'4px 10px',fontSize:'0.85rem'}}>Remove</button>
              </div>
            ))}
          </div>
        </details>

        <details className="settings-details" open={openFaqs} onToggle={e=>setOpenFaqs(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openFaqs)} aria-hidden="true">▼</span>FAQs</summary>
          <div style={DETAILS_BODY}>
            <div style={{border:'1px solid var(--truva-border)',padding:10,borderRadius:6,marginBottom:10}}>
              <input style={IS} placeholder="Question" value={newFaq.question} onChange={e=>setNewFaq({...newFaq,question:e.target.value})} />
              <textarea style={IS} placeholder="Answer" value={newFaq.answer} onChange={e=>setNewFaq({...newFaq,answer:e.target.value})} />
              <button type="button" style={BTN} onClick={handleAddFaq}>Add FAQ</button>
            </div>
            {faqs.map((faq,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'start',padding:'8px 0',borderBottom:'1px solid var(--truva-border)'}}>
                <div><strong>Q: {faq.question}</strong><br/><span style={{color:'var(--truva-text-secondary)',fontSize:'0.95rem'}}>A: {faq.answer}</span></div>
                <button type="button" onClick={()=>handleRemoveFaq(i)} style={{...BTN,backgroundColor:'var(--truva-danger-red)',padding:'4px 10px',fontSize:'0.85rem'}}>Remove</button>
              </div>
            ))}
          </div>
        </details>
        <details className="settings-details" open={openRecording} onToggle={e=>setOpenRecording(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openRecording)} aria-hidden="true">&#9660;</span>Call transcripts</summary>
          <div style={DETAILS_BODY}>
            <p style={{margin:'0 0 10px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
              On Concierge and Business, Truva can save a text transcript and short recap for every call.
              Audio is recorded only long enough to transcribe, then deleted unless you keep it below.
            </p>
            <div style={{marginBottom:8}}>
              <span style={{display:'block',fontSize:'0.95rem',color:'var(--truva-text-secondary)',marginBottom:8}}>Mode</span>
              <span style={{display:'flex',gap:8,border:'1px solid var(--truva-border)',borderRadius:10,padding:4,background:'var(--truva-surface-2)',opacity:canEdit?1:0.7}}>
                {[['off','Off'],['on_demand','On demand'],['always','Always']].map(([val,label])=>(
                  <button key={val} type="button" disabled={!canEdit} onClick={async ()=>{
                    if (!canEdit) return;
                    setRecordingMode(val);
                    await saveLinePatch({ recording_mode: val, record_calls: val !== 'off' });
                  }} style={{flex:1,padding:'8px 0',border:'none',borderRadius:8,fontSize:'0.95rem',cursor:canEdit?'pointer':'default',background:recordingMode===val?'var(--truva-action-blue)':'transparent',color:recordingMode===val?'#fff':'var(--truva-text)'}}>{label}</button>
                ))}
              </span>
              <span style={{display:'block',fontSize:'0.9rem',color:'var(--truva-text-tertiary)',fontWeight:400,marginTop:6}}>
                Always (default on Concierge/Business) records from connect, builds a transcript after the call, then deletes the audio. On demand only records when you tap Record. Off records nothing.
              </span>
            </div>
            <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:canEdit?'pointer':'default',padding:'2px 0'}}>
              <input type="checkbox" checked={recordConsentAnnounce} disabled={!canEdit} onChange={async e=>{
                if (!canEdit) return;
                const v = e.target.checked;
                setRecordConsentAnnounce(v);
                await saveLinePatch({ record_consent_announce: v });
              }} />
              <span style={{fontSize:'0.95rem',fontWeight:400}}>Announce a notice to the caller before recording starts</span>
            </label>
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:canEdit?'pointer':'default',padding:'2px 0'}}>
              <input type="checkbox" checked={keepAudio} disabled={!canEdit} onChange={async e=>{
                if (!canEdit) return;
                const v = e.target.checked;
                setKeepAudio(v);
                await saveLinePatch({ keep_audio: v });
              }} />
              <span style={{fontSize:'0.95rem',fontWeight:400}}>Keep call audio after the transcript is generated</span>
            </label>
          </div>
        </details>

        <details className="settings-details" open={openKnowledge} onToggle={e=>setOpenKnowledge(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openKnowledge)} aria-hidden="true">▼</span>Knowledge Base</summary>
          <div style={DETAILS_BODY}>
            <p style={{margin:'0 0 8px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
              Extra facts your AI receptionist should know — anything not covered by
              Services and FAQs above. Markdown supported. This overrides any legacy
              knowledge file for this line.
            </p>
            <textarea
              rows={10}
              style={{...IS, fontFamily:'monospace', fontSize:'0.9rem', resize:'vertical'}}
              value={knowledgeBase}
              onChange={e=>{setKnowledgeBase(e.target.value); setKnowledgeDirty(true);}}
              placeholder={"# My business\n- Family-owned since 1998\n- We never answer calls with robocalls\n## Policies\n- Cancellations accepted up to 1 hour before"}
            />
            <div style={{display:'flex',alignItems:'center',gap:10,marginTop:8}}>
              <button type="button" style={BTN} disabled={!knowledgeDirty} onClick={async ()=>{
                try {
                  const r = await fetch(API_BASE+'/api/agent/line-settings',{method:'PUT',headers:{Authorization:'Bearer '+authToken,'Content-Type':'application/json'},body:JSON.stringify({number:selectedNumber,knowledge_base:knowledgeBase})});
                  if (r.ok) { setKnowledgeDirty(false); setKnowledgeSavedAt(new Date().toLocaleTimeString()); }
                } catch(e) {}
              }}>Save Knowledge Base</button>
              <span style={{fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
                {knowledgeSavedAt ? `Saved at ${knowledgeSavedAt}` : (knowledgeBase ? `${knowledgeBase.length} chars` : 'Empty — AI uses only Services/FAQs')}
              </span>
            </div>
          </div>
        </details>

        <details className="settings-details" open={openClosedMsg} onToggle={e=>setOpenClosedMsg(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openClosedMsg)} aria-hidden="true">▼</span>Closed Message</summary>
          <div style={DETAILS_BODY}>
            <textarea rows={3} style={IS} value={afterHoursMessage} onChange={e=>setAfterHoursMessage(e.target.value)}
              placeholder="You've reached us after hours. Please leave a message." />
            <button type="button" style={BTN} onClick={handleSaveClosedMessage}>Save Closed Message</button>
          </div>
        </details>

        <details className="settings-details" open={openAccount} onToggle={e=>{setOpenAccount(e.target.open); if(!e.target.open){setDeleteConfirming(false); setDeleteError('');}}} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openAccount)} aria-hidden="true">▼</span>Account</summary>
          <div style={DETAILS_BODY}>
            <p style={{margin:'0 0 10px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
              Deleting your account removes your access and personal info from Truva.
              Call and message history for your team stays intact for other members.
              Your Solvit Hub sign-in still works for any other apps you use.
            </p>
            {deleteError && <p style={{color:'var(--truva-danger-text)',fontSize:'0.9rem',margin:'0 0 8px'}}>{deleteError}</p>}
            {!deleteConfirming ? (
              <button type="button" style={{...BTN,backgroundColor:'var(--truva-danger-red)'}} onClick={()=>{setDeleteConfirming(true); setDeleteError('');}}>Delete Account</button>
            ) : (
              <div>
                <p style={{margin:'0 0 10px',fontSize:'0.95rem',fontWeight:600,color:'var(--truva-danger-text)'}}>
                  This can't be undone. Delete your Truva account?
                </p>
                <div style={{display:'flex',gap:8}}>
                  <button type="button" disabled={acctDeleteBusy} style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)'}} onClick={()=>setDeleteConfirming(false)}>Cancel</button>
                  <button type="button" disabled={acctDeleteBusy} style={{...BTN,backgroundColor:'var(--truva-danger-red)'}} onClick={async ()=>{
                    setAcctDeleteBusy(true); setDeleteError('');
                    try {
                      const r = await fetch(API_BASE+'/api/account',{method:'DELETE',headers:{Authorization:'Bearer '+authToken}});
                      if (r.ok) { if (onAccountDeleted) onAccountDeleted(); return; }
                      const body = await r.json().catch(()=>({}));
                      setDeleteError(body.message || body.error || 'Could not delete account.');
                    } catch (e) {
                      setDeleteError('Could not reach the server. Try again.');
                    } finally {
                      setAcctDeleteBusy(false);
                    }
                  }}>{acctDeleteBusy?'Deleting…':'Yes, delete my account'}</button>
                </div>
              </div>
            )}
          </div>
        </details>
        </fieldset>
        </>
        </ErrorBoundary>
      )}

      {/* === VOICE AGENT TAB === */}
      {tab === 'agent' && (
        <ErrorBoundary inline label="Agent">
        <fieldset disabled={!canEdit} style={{border:'none',margin:0,padding:0,minWidth:0}}>
        <section style={{...SS, padding:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <h3 style={{margin:0,fontSize:'0.95rem',color:'var(--truva-text)'}}>AI Receptionist</h3>
              <p style={{margin:'2px 0 0',fontSize:'0.9rem',color:mode==='ai'?'var(--truva-success-text)':'var(--truva-text-tertiary)'}}>{mode==='ai'?'Active — AI answers this line':'Off — this line rings Truva / voicemail'}</p>
            </div>
            <label style={{position:'relative',display:'inline-block',width:48,height:26,flexShrink:0}}>
              <input type="checkbox" checked={mode==='ai'} disabled={!canEdit} onChange={async e=>{
                if (!canEdit) return;
                const newMode=e.target.checked?'ai':'human'; setMode(newMode);
                try{
                  const body={mode:newMode,inbound_mode:newMode};
                  if (selectedNumber) body.number=selectedNumber;
                  const r=await fetch(API_BASE+'/api/agent/inbound-mode',{method:'PUT',headers:{Authorization:'Bearer '+authToken,'Content-Type':'application/json'},body:JSON.stringify(body)});
                  if(!r.ok) setMode(mode);
                }
                catch{setMode(mode);}
              }} style={{opacity:0,width:0,height:0}} />
              <span style={{position:'absolute',cursor:canEdit?'pointer':'default',top:0,left:0,right:0,bottom:0,background:mode==='ai'?'var(--truva-action-blue)':'var(--truva-surface-3)',borderRadius:26,transition:'.3s',opacity:canEdit?1:0.7}}>
                <span style={{position:'absolute',content:'\"\"',height:20,width:20,left:mode==='ai'?25:3,bottom:3,background:'white',borderRadius:'50%',transition:'.3s'}} />
              </span>
            </label>
          </div>
          {mode==='ai' && (
            <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid var(--truva-border)'}}>
              <p style={{margin:'0 0 8px',fontSize:'0.95rem',fontWeight:600,color:'var(--truva-text-secondary)'}}>Call language</p>
              <p style={{margin:'0 0 8px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
                Each call stays in one language. Auto-detect works like Live Translate: listen to the caller&apos;s first words, lock that language once (same language list), and do not switch mid-call. Silence or uncertain speech falls back to English.
              </p>
              <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:canEdit?'pointer':'default',padding:'2px 0'}}>
                <input type="radio" name="rosieLang" checked={rosieLangMode==='en_es'} disabled={!canEdit} onChange={()=>{setRosieLangMode('en_es'); saveLinePatch({ rosie_lang_mode: 'en_es' });}} />
                <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>English + Spanish (press 1 or 2, then stay)</span>
              </label>
              <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:canEdit?'pointer':'default',padding:'2px 0'}}>
                <input type="radio" name="rosieLang" checked={rosieLangMode==='en_es_auto'} disabled={!canEdit} onChange={()=>{setRosieLangMode('en_es_auto'); saveLinePatch({ rosie_lang_mode: 'en_es_auto' });}} />
                <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>Auto-detect language (lock for call — no keypad)</span>
              </label>
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:canEdit?'pointer':'default',padding:'2px 0'}}>
                <input type="radio" name="rosieLang" checked={rosieLangMode==='en'} disabled={!canEdit} onChange={()=>{setRosieLangMode('en'); saveLinePatch({ rosie_lang_mode: 'en' });}} />
                <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>English only</span>
              </label>
            </div>
          )}
          {mode==='human' && (
            <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid var(--truva-border)'}}>
              <p style={{margin:'0 0 8px',fontSize:'0.95rem',fontWeight:600,color:'var(--truva-text-secondary)'}}>When a call comes in:</p>
              <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:canEdit?'pointer':'default',padding:'2px 0'}}>
                <input type="radio" name="offMode" checked={offMode==='ring'} disabled={!canEdit} onChange={()=>{setOffMode('ring'); saveLinePatch({ off_mode: 'ring' });}} />
                <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>Ring Truva phone</span>
              </label>
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:canEdit?'pointer':'default',padding:'2px 0'}}>
                <input type="radio" name="offMode" checked={offMode==='forward'} disabled={!canEdit} onChange={()=>{setOffMode('forward'); saveLinePatch({ off_mode: 'forward' });}} />
                <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>Forward to {transferNumber||'another number'}</span>
              </label>
            </div>
          )}
        </section>

        <details className="settings-details" open={openVoiceAgent} onToggle={e=>setOpenVoiceAgent(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openVoiceAgent)} aria-hidden="true">▼</span>Agent Voice</summary>
          <div style={DETAILS_BODY}>
          <p style={{margin:'0 0 10px',fontSize:'0.95rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
            Choose who answers inbound calls. Male, female, and other Gemini Live voices.
          </p>
          <select
            style={IS}
            value={voicePreference}
            onChange={e=>setVoicePreference(e.target.value)}
            aria-label="Agent voice"
          >
            {voicesByGender('female').length > 0 && (
              <optgroup label="Female">
                {voicesByGender('female').map(v => (
                  <option key={v.id} value={v.id}>{v.label} — {v.description}</option>
                ))}
              </optgroup>
            )}
            {voicesByGender('male').length > 0 && (
              <optgroup label="Male">
                {voicesByGender('male').map(v => (
                  <option key={v.id} value={v.id}>{v.label} — {v.description}</option>
                ))}
              </optgroup>
            )}
            {voicesByGender('other').length > 0 && (
              <optgroup label="Other">
                {voicesByGender('other').map(v => (
                  <option key={v.id} value={v.id}>{v.label} — {v.description}</option>
                ))}
              </optgroup>
            )}
            {voiceOptions.length === 0 && (
              <>
                <option value="Puck">Puck — Young, energetic, bright</option>
                <option value="Charon">Charon — Calm, informative, measured</option>
                <option value="Fenrir">Fenrir — Deep, confident</option>
                <option value="Orus">Orus — Warm, neutral</option>
                <option value="Zephyr">Zephyr — Light, youthful</option>
                <option value="Kore">Kore — Firm, calm</option>
                <option value="Aoede">Aoede — Bright, expressive</option>
                <option value="Leda">Leda — Warm, melodic</option>
              </>
            )}
          </select>
          <button type="button" style={BTN} onClick={handleSaveVoice}>Save Voice</button>
          </div>
        </details>

        <details className="settings-details" open={openGreetingAgent} onToggle={e=>setOpenGreetingAgent(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openGreetingAgent)} aria-hidden="true">▼</span>Greeting</summary>
          <div style={DETAILS_BODY}>
          <textarea rows={3} style={IS} value={greeting} onChange={e=>setGreeting(e.target.value.slice(0,500))}
            onBlur={()=>saveConfig({location:{greeting}})}
            placeholder="Thanks for calling Acme. How can I help you today?" />
          <button onClick={handleHearGreeting} disabled={hearing} style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',marginTop:6}}>{hearing?'Playing...':'Hear it'}</button>
          {audioSrc&&<audio controls autoPlay src={audioSrc} style={{marginTop:6,width:'100%',height:32}} onEnded={()=>setAudioSrc(null)}/>}
          </div>
        </details>

        <details className="settings-details" open={openVoicemail} onToggle={e=>setOpenVoicemail(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openVoicemail)} aria-hidden="true">▼</span>Voicemail Greeting</summary>
          <div style={DETAILS_BODY}>
          <textarea rows={3} style={IS} value={voicemailGreeting} onChange={e=>setVoicemailGreeting(e.target.value.slice(0,500))}
            onBlur={async ()=>{
              if (selectedNumber && authToken) {
                try {
                  await fetch(API_BASE+'/api/agent/line-settings',{
                    method:'PUT',
                    headers:{Authorization:'Bearer '+authToken,'Content-Type':'application/json'},
                    body:JSON.stringify({number:selectedNumber,voicemail_greeting:voicemailGreeting}),
                  });
                  setSaveStatus('Saved'); setTimeout(()=>setSaveStatus(''),1500);
                } catch (_) { setError('Voicemail save failed'); }
              } else {
                saveConfig({location:{voicemail_greeting:voicemailGreeting}});
              }
            }}
            placeholder="You've reached [Business Name]. We can't take your call right now. Please leave a message and we'll get back to you." />
          {!selectedNumber && (
            <p style={{margin:'6px 0 0',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
              No line selected — saving as the shared business default.
            </p>
          )}
          </div>
        </details>

        <details className="settings-details" open={openTransferAgent} onToggle={e=>setOpenTransferAgent(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openTransferAgent)} aria-hidden="true">▼</span>Transfer to Human</summary>
          <div style={DETAILS_BODY}>
          <input type="tel" style={IS} value={transferNumber} onChange={e=>setTransferNumber(e.target.value)}
                      onBlur={()=>saveConfig({location:{transfer_number:transferNumber}})} placeholder="(555) 123-4567" />
                    <p style={{margin:'12px 0 6px',fontSize:'0.95rem',fontWeight:600,color:'var(--truva-text-secondary)'}}>Owner cell (alerts)</p>
                    <input type="tel" style={IS} value={ownerCell} onChange={e=>setOwnerCell(e.target.value)}
                      onBlur={()=>saveConfig({location:{owner_cell:ownerCell}})} placeholder="(555) 987-6543" />
                    <p style={{margin:'6px 0 0',fontSize:'0.8rem',color:'var(--truva-text-tertiary)'}}>Voicemail and missed-call alerts text this number. Falls back to the transfer number when empty.</p>
                    <p style={{margin:'12px 0 6px',fontSize:'0.95rem',fontWeight:600,color:'var(--truva-text-secondary)'}}>Handoff message</p>
                              <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:'pointer',padding:'2px 0'}}>
                                <input type="radio" name="transferMode" checked={transferMessageMode==='summary'} onChange={()=>{setTransferMessageMode('summary'); saveConfig({location:{transfer_message_mode:'summary'}});}} />
                                <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>AI summarizes the conversation (~15-25s)</span>
                              </label>
                              <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:'pointer',padding:'2px 0'}}>
                                <input type="radio" name="transferMode" checked={transferMessageMode==='custom'} onChange={()=>{setTransferMessageMode('custom'); saveConfig({location:{transfer_message_mode:'custom'}});}} />
                                <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>Custom message</span>
                              </label>
                              {transferMessageMode==='custom' && (
                                <textarea rows={2} style={{...IS, resize:'vertical'}} value={transferMessage} onChange={e=>setTransferMessage(e.target.value)}
                                  onBlur={()=>saveConfig({location:{transfer_message:transferMessage}})} placeholder="One moment please, connecting you now..." />
                              )}
          <p style={{margin:'12px 0 6px',fontSize:'0.95rem',fontWeight:600,color:'var(--truva-text-secondary)'}}>When to transfer:</p>
          <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:'pointer',padding:'2px 0'}}>
            <input type="checkbox" checked={!!transferTriggers.caller_asks} onChange={e=>{
              const v={...transferTriggers,caller_asks:e.target.checked}; setTransferTriggers(v);
              saveConfig({location:{transfer_triggers:v}});
            }} />
            <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>Caller asks for a person</span>
          </label>
          <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:'pointer',padding:'2px 0'}}>
            <input type="checkbox" checked={!!transferTriggers.sentiment_escalation} onChange={e=>{
              const v={...transferTriggers,sentiment_escalation:e.target.checked}; setTransferTriggers(v);
              saveConfig({location:{transfer_triggers:v}});
            }} />
            <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>Caller sounds frustrated or angry</span>
          </label>
          <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:'pointer',padding:'2px 0'}}>
            <input type="checkbox" checked={!!transferTriggers.after_n_loops} onChange={e=>{
              const v={...transferTriggers,after_n_loops:e.target.checked?(transferTriggers.after_n_loops||2):0}; setTransferTriggers(v);
              saveConfig({location:{transfer_triggers:v}});
            }} />
            <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>After AI cannot resolve after</span>
            <select value={transferTriggers.after_n_loops} style={{padding:'2px 6px',fontSize:'0.9rem',marginLeft:4}} onChange={e=>{
              const v={...transferTriggers,after_n_loops:parseInt(e.target.value)}; setTransferTriggers(v);
              saveConfig({location:{transfer_triggers:v}});
            }}>
              {[1,2,3,4,5].map(n=><option key={n} value={n}>{n} {n===1?'attempt':'attempts'}</option>)}
            </select>
          </label>
          <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:'pointer',padding:'2px 0'}}>
            <input type="checkbox" checked={!!transferTriggers.business_hours_only} onChange={e=>{
              const v={...transferTriggers,business_hours_only:e.target.checked}; setTransferTriggers(v);
              saveConfig({location:{transfer_triggers:v}});
            }} />
            <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>During business hours only</span>
          </label>
          </div>
        </details>

        <details className="settings-details" open={openAlerts} onToggle={e=>setOpenAlerts(e.target.open)} style={SS}>
          <summary style={SUMMARY}><span style={CHEVRON(openAlerts)} aria-hidden="true">▼</span>Alerts &amp; Notifications</summary>
          <div style={DETAILS_BODY}>
          <p style={{margin:'0 0 8px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
            Email alerts for this line. Add as many addresses as you need — leave the list empty to use your account admin email.
          </p>
          {alertEmails.length === 0 && (
            <p style={{margin:'0 0 8px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
              No addresses yet — alerts go to your account admin email.
            </p>
          )}
          {alertEmails.map((addr, i) => (
            <div key={i} style={{display:'flex',gap:6,alignItems:'center',marginBottom:6}}>
              <input type="email" inputMode="email" style={IS} value={addr}
                onChange={e=>{ const v=[...alertEmails]; v[i]=e.target.value; setAlertEmails(v); }}
                onBlur={()=>saveAlertEmails(alertEmails)}
                placeholder="owner@example.com" />
              <button type="button" aria-label="Remove email"
                onClick={()=>{ const v = alertEmails.filter((_,j)=>j!==i); setAlertEmails(v); saveAlertEmails(v); }}
                style={{...BTN,flex:'none',backgroundColor:'var(--truva-btn-secondary-bg)'}}>Remove</button>
            </div>
          ))}
          <button type="button" onClick={()=>setAlertEmails([...alertEmails,''])}
            style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',marginTop:2}}>Add email</button>
          <p style={{margin:'14px 0 6px',fontSize:'0.95rem',fontWeight:600,color:'var(--truva-text-secondary)'}}>Email me when:</p>
          {Object.keys(ALERT_EVENT_LABELS).map(key => (
            <label key={key} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,cursor:'pointer',padding:'2px 0'}}>
              <input type="checkbox" checked={!!alertEvents[key]}
                onChange={e=>saveAlertEvent(key, e.target.checked)} />
              <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>{ALERT_EVENT_LABELS[key]}</span>
            </label>
          ))}
          <p style={{margin:'8px 0 0',fontSize:'0.8rem',color:'var(--truva-text-tertiary)'}}>
            Push notifications to the Truva mobile app (new text, new voicemail, missed call, call summary, transfer problems) are sent automatically.
          </p>
          </div>
        </details>

        </fieldset>
        </ErrorBoundary>
      )}

      {/* === CHATBOT TAB === */}
      {tab === 'chatbot' && (
        <ErrorBoundary inline label="Chatbot">
        <fieldset disabled={!canEdit} style={{border:'none',margin:0,padding:0,minWidth:0}}>
        <section style={{...SS, padding:'12px 14px'}}>
          <div style={{fontSize:'0.95rem', fontWeight:600, color:'var(--truva-text)', marginBottom:8}}>
            AI Text Replies (SMS)
          </div>
          <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,fontSize:'0.95rem',color:'var(--truva-text)'}}>
            <input type="checkbox" checked={chatbotEnabled||false} disabled={!canEdit || !selectedNumber} onChange={async e=>{
              const on=e.target.checked; setChatbotEnabled(on);
              const ok = await saveLinePatch({ chatbot_enabled: on, chatbot_prompt: chatbotPrompt });
              if (!ok) setChatbotEnabled(!on);
            }} />
            Enable auto-reply to incoming SMS
          </label>
          {chatbotEnabled && <>
            <textarea rows={6} style={IS} value={chatbotPrompt} disabled={!canEdit || !selectedNumber} onChange={e=>setChatbotPrompt(e.target.value)}
              onBlur={async()=>{await saveLinePatch({ chatbot_enabled: true, chatbot_prompt: chatbotPrompt });}}
              placeholder="You are a helpful assistant for our business. Answer customer questions about hours, services, and pricing." />
            {chatbotError&&<p style={{color:'var(--truva-danger-text)',fontSize:'0.9rem'}}>{chatbotError}</p>}
          </>}
        </section>
        </fieldset>
        </ErrorBoundary>
      )}

      {/* === TRANSLATE TAB === */}
      {tab === 'translate' && (
        <ErrorBoundary inline label="Translate">
        <>
        <fieldset disabled={!canEdit} style={{border:'none',margin:0,padding:0,minWidth:0}}>
        <section style={{...SS, padding:'12px 14px'}}>
          <p style={{margin:'0 0 12px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
            Language pair, persona, and SMS mode are saved for this Line
            {selectedNumber ? ` (${lineLabel})` : ''} only — other numbers keep their own values.
            Both translate modes below use the same language pair.
          </p>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:'0.95rem',color:'var(--truva-text-secondary)',display:'block',marginBottom:4}}>Your language (this line)</label>
            <select style={IS} value={translate.userLang} onChange={e=>updateTranslate({userLang:e.target.value})} disabled={!canEdit || !selectedNumber}>
              {Object.entries(TRANSLATE_LANGUAGES).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:'0.95rem',color:'var(--truva-text-secondary)',display:'block',marginBottom:4}}>Other person&apos;s language (this line)</label>
            <select style={IS} value={translate.remoteLang} onChange={e=>updateTranslate({remoteLang:e.target.value})} disabled={!canEdit || !selectedNumber}>
              {Object.entries(TRANSLATE_LANGUAGES).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:'0.95rem',color:'var(--truva-text-secondary)',display:'block',marginBottom:4}}>Persona (this line)</label>
            <select style={IS} value={translate.domain} onChange={e=>updateTranslate({domain:e.target.value})} disabled={!canEdit || !selectedNumber}>
              {Object.entries(TRANSLATE_DOMAINS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {translateSaved && canEdit && <p style={{color:'var(--truva-success-text)',fontSize:'0.9rem'}}>Saved</p>}
        </section>

        <section style={{...SS, marginTop:12, padding:'12px 14px'}}>
          <h3 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Live Translate — Phone Calls</h3>
          <p style={{margin:0,fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
            Translates both sides of an active call in real time. Turn it on with the
            Translate button that appears once you&apos;re on a call — nothing to activate here.
          </p>
        </section>

        <section style={{...SS, marginTop:12, padding:'12px 14px'}}>
          <h3 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>In-Person Translate</h3>
          <p style={{margin:'0 0 12px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
            Translate a face-to-face conversation using this phone&apos;s microphone —
            no call involved. Hold the phone between you and the other person.
          </p>
          <button
            type="button"
            style={{...BTN, backgroundColor: inPersonTranslateActive ? 'var(--truva-danger-red)' : 'var(--truva-action-blue)'}}
            disabled={!canEdit}
            onClick={() => onToggleInPersonTranslate?.()}
          >
            {inPersonTranslateActive ? 'Stop In-Person Translate' : 'Start In-Person Translate'}
          </button>
        </section>
        </fieldset>

        <section style={{...SS, marginTop:12, padding:'12px 14px'}}>
          <h4 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>SMS Translate</h4>
          <select style={IS} value={translate.smsMode||'ask'} onChange={e=>updateSmsMode(e.target.value)} disabled={!selectedNumber}>
            {Object.entries(SMS_TRANSLATE_MODES).map(([k,v])=><option key={k} value={k}>{v}</option>)}
          </select>
        </section>
        </>
        </ErrorBoundary>
      )}

      {/* === SCHEDULING TAB === */}
      {tab === 'scheduling' && (
        <ErrorBoundary inline label="Scheduling">
        <section style={{...SS, padding:16}}>
          <h3 style={{margin:'0 0 8px',fontSize:'0.95rem',color:'var(--truva-text)'}}>AI Appointment Scheduling</h3>
          <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
            <input type="checkbox" checked={schedulingEnabled||false} disabled={!canEdit || !selectedNumber} onChange={async e=>{
              const on=e.target.checked; setSchedulingEnabled(on);
              const ok = await saveLinePatch({ scheduling_enabled: on });
              if (!ok) setSchedulingEnabled(!on);
            }} />
            Let the AI check availability &amp; book appointments on this line
          </label>
          {!selectedNumber && (
            <p style={{margin:'0 0 16px',fontSize:'0.9rem',color:'var(--truva-warn-text)'}}>Select a Line above to turn this on for it.</p>
          )}

          {gcal && (gcal.oauth_configured || gcal.connected) && (
            <div style={{border:'1px solid var(--truva-border)',borderRadius:8,padding:'10px 12px',marginBottom:16}}>
              <h4 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Google Calendar</h4>
              <p style={{margin:'0 0 10px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
                {gcal.connected
                  ? `Booked appointments are added to ${gcal.email || gcal.calendar_id || 'your calendar'}; cancelling one removes the event.`
                  : 'Connect your Google account once and every booked appointment is added to your calendar. Cancellations remove the event.'}
              </p>
              <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                {gcal.connected ? (
                  <button type="button" style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',padding:'6px 14px'}}
                    disabled={gcalBusy||!canEditScheduling} onClick={handleGcalDisconnect}>
                    {gcalBusy?'Working...':'Disconnect'}
                  </button>
                ) : (
                  <button type="button" style={{...BTN,padding:'6px 14px'}}
                    disabled={gcalBusy||!canEditScheduling} onClick={handleGcalConnect}>
                    {gcalBusy?'Connecting...':'Connect Google Calendar'}
                  </button>
                )}
                <span style={{fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
                  {gcal.connected ? 'Connected' : 'Not connected'}
                </span>
              </div>
            </div>
          )}

          <h4 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Attendant week</h4>
          <p style={{margin:'0 0 10px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
            Who works each shift. One person per cell, or blank for nobody - blank cells are not offered by the AI.
            Changes apply to the week shown only, so you can fill in next week while this one is still running.
          </p>
          <div style={{marginBottom:14,padding:'10px 12px',borderRadius:8,border:'1px solid var(--truva-border)',background:'var(--truva-surface-2)'}}>
            <div style={{fontSize:'0.88rem',fontWeight:600,color:'var(--truva-text)',marginBottom:2}}>Time slots</div>
            <p style={{margin:'0 0 10px',fontSize:'0.82rem',color:'var(--truva-text-tertiary)'}}>
              The rows of the grid above. Slots must not overlap. Existing assignments keep
              the times they were saved with; changing a slot only changes the grid from now on.
            </p>
            {slotDraft.map((s, idx) => (
              <div key={idx} style={SLOT_ROW}>
                <input type="time" value={s.start} disabled={!canEditScheduling || slotsSaving} style={SLOT_TIME}
                  onChange={e=>setSlotDraft(prev=>prev.map((r,i)=>i===idx?{...r,start:e.target.value}:r))}
                  onBlur={()=>saveSlots(slotDraft)} />
                <span style={{color:'var(--truva-text-tertiary)'}}>&ndash;</span>
                <input type="time" value={s.end} disabled={!canEditScheduling || slotsSaving} style={SLOT_TIME}
                  onChange={e=>setSlotDraft(prev=>prev.map((r,i)=>i===idx?{...r,end:e.target.value}:r))}
                  onBlur={()=>saveSlots(slotDraft)} />
                <button type="button" aria-label="Delete slot" disabled={!canEditScheduling || slotsSaving}
                  onClick={()=>handleDeleteSlot(idx)} style={SLOT_BTN}>&times;</button>
              </div>
            ))}
            {slotDraftLoaded && slotDraft.length === 0 && (
              <p style={{margin:'0 0 8px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>No slots yet &mdash; add one below.</p>
            )}
            <div style={{...SLOT_ROW,marginTop:8}}>
              <input type="time" value={newSlot.start} disabled={!canEditScheduling || slotsSaving} style={SLOT_TIME}
                onChange={e=>setNewSlot(v=>({...v,start:e.target.value}))} />
              <span style={{color:'var(--truva-text-tertiary)'}}>&ndash;</span>
              <input type="time" value={newSlot.end} disabled={!canEditScheduling || slotsSaving} style={SLOT_TIME}
                onChange={e=>setNewSlot(v=>({...v,end:e.target.value}))} />
              <button type="button" disabled={!canEditScheduling || slotsSaving} onClick={handleAddSlot}
                style={{...BTN,padding:'6px 12px',fontSize:'0.85rem',flex:'none'}}>Add</button>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
            <button type="button" style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',padding:'6px 12px'}}
              onClick={()=>{ const d=new Date(rosterWeekStart); d.setDate(d.getDate()-7); setRosterWeekStart(mondayOfWeek(d)); }}>&#8249; Prev</button>
            <span style={{fontSize:'0.95rem',fontWeight:600,color:'var(--truva-text)',minWidth:130,textAlign:'center'}}>
              {roster
                ? `${new Date(roster.week_start + 'T00:00:00').toLocaleDateString(undefined,{month:'short',day:'numeric'})} - ${new Date(roster.week_end + 'T00:00:00').toLocaleDateString(undefined,{month:'short',day:'numeric'})}`
                : 'Loading...'}
            </span>
            <button type="button" style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',padding:'6px 12px'}}
              onClick={()=>{ const d=new Date(rosterWeekStart); d.setDate(d.getDate()+7); setRosterWeekStart(mondayOfWeek(d)); }}>Next &#8250;</button>
            <button type="button" style={{...BTN,padding:'6px 14px'}} disabled={!roster||rosterSaving||!canEditScheduling}
              onClick={handleSaveRoster}>{rosterSaving?'Saving...':'Save week'}</button>
            <button type="button" disabled={!roster||rosterSaving||!canEditScheduling||rosterCopying}
              style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',color:'var(--truva-text)',padding:'6px 14px'}}
              onClick={()=> (rosterCopyOpen ? setRosterCopyOpen(false) : openRosterCopy())}>
              {rosterCopyOpen ? 'Close copy' : 'Copy week'}
            </button>
          </div>
          {rosterCopyOpen && canEditScheduling && (
            <div style={{border:'1px solid var(--truva-border)',borderRadius:8,background:'var(--truva-surface-2)',padding:'12px 14px',marginBottom:14,fontSize:'0.85rem',color:'var(--truva-text)'}}>
              <div style={{fontWeight:600,marginBottom:6}}>Copy week</div>
              <p style={{margin:'0 0 10px',color:'var(--truva-text-secondary)'}}>
                Copies {roster ? fmtWeekRange(roster.week_start) : 'the week shown'} onto the weeks below &mdash;
                same weekdays, same slot times, same attendants.
              </p>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
                <label style={{display:'flex',alignItems:'center',gap:6}}>
                  Copy to
                  <input type="date" value={rosterCopyStart} disabled={rosterCopying}
                    onChange={e=>{
                      const v = e.target.value;
                      setRosterCopyStart(v ? fmtDateLocal(mondayOfWeek(new Date(v + 'T00:00:00'))) : '');
                      setRosterCopyResult(null);
                    }}
                    style={{padding:'6px 8px',fontSize:'0.85rem',borderRadius:6,border:'1px solid var(--truva-border)',background:'var(--truva-surface)',color:'var(--truva-text)'}} />
                </label>
                <label style={{display:'flex',alignItems:'center',gap:6}}>
                  for
                  <input type="number" min="1" max="12" value={rosterCopyWeeks} disabled={rosterCopying}
                    onChange={e=>{
                      const n = Number(e.target.value);
                      setRosterCopyWeeks(Number.isFinite(n) ? Math.max(1, Math.min(12, Math.round(n))) : 1);
                      setRosterCopyResult(null);
                    }}
                    style={{width:64,padding:'6px 8px',fontSize:'0.85rem',borderRadius:6,border:'1px solid var(--truva-border)',background:'var(--truva-surface)',color:'var(--truva-text)'}} />
                  week{rosterCopyTargets.length === 1 ? '' : 's'}
                </label>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:6,marginBottom:10,color:'var(--truva-text-secondary)'}}>
                <input type="checkbox" checked={rosterCopyOverwrite} disabled={rosterCopying}
                  onChange={e=>{ setRosterCopyOverwrite(e.target.checked); setRosterCopyResult(null); }} />
                Overwrite weeks that already have a schedule
              </label>
              <div style={{marginBottom:10,color:'var(--truva-text-secondary)'}}>
                {rosterCopyTargets.length
                  ? `Will copy to ${rosterCopyTargets.length} week${rosterCopyTargets.length === 1 ? '' : 's'}: ${rosterCopyTargets.map(fmtWeekRange).join(', ')}`
                  : 'Pick the first week to copy to.'}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button type="button" style={{...BTN,padding:'6px 14px'}}
                  disabled={rosterCopying||!roster||!rosterCopyTargets.length}
                  onClick={handleCopyRoster}>{rosterCopying ? 'Copying...' : 'Copy'}</button>
                <button type="button" disabled={rosterCopying}
                  style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',color:'var(--truva-text)',padding:'6px 12px'}}
                  onClick={()=>setRosterCopyOpen(false)}>Cancel</button>
              </div>
              {rosterCopyResult && (
                <div style={{marginTop:10,lineHeight:1.5}}>
                  {rosterCopyResult.copied.length > 0 && (
                    <div>{rosterCopyResult.copied.length === 1 ? 'Copied to ' : 'Copied to: '}
                      {rosterCopyResult.copied.map(c => fmtWeekRange(c.week_start)).join(', ')}</div>
                  )}
                  {rosterCopyResult.skipped.length > 0 && (
                    <div style={{color:'var(--truva-text-secondary)'}}>Skipped, already has a schedule:
                      {' '}{rosterCopyResult.skipped.map(fmtWeekRange).join(', ')}
                      {rosterCopyOverwrite ? '' : ' (tick overwrite to replace them)'}</div>
                  )}
                  {rosterCopyResult.inactive > 0 && (
                    <div style={{color:'var(--truva-text-secondary)'}}>
                      {rosterCopyResult.inactive} shift{rosterCopyResult.inactive === 1 ? '' : 's'} not copied &mdash; that attendant is no longer active.
                    </div>
                  )}
                  {rosterCopyResult.copied.length === 0 && rosterCopyResult.skipped.length === 0 && (
                    <div style={{color:'var(--truva-text-secondary)'}}>Nothing was copied.</div>
                  )}
                </div>
              )}
            </div>
          )}
          {roster && (
            <div style={{overflowX:'auto',marginBottom:16}}>
              <div style={{display:'grid',gridTemplateColumns:'110px repeat(7, minmax(92px, 1fr))',gap:4,minWidth:760}}>
                <div />
                {roster.days.map(day => (
                  <div key={day.date} style={{fontSize:'0.8rem',fontWeight:600,textAlign:'center',color:'var(--truva-text-secondary)'}}>
                    {new Date(day.date + 'T00:00:00').toLocaleDateString(undefined,{weekday:'short'})}
                    <div style={{fontWeight:400,color:'var(--truva-text-tertiary)'}}>
                      {new Date(day.date + 'T00:00:00').toLocaleDateString(undefined,{month:'numeric',day:'numeric'})}
                    </div>
                  </div>
                ))}
                {roster.slots.length === 0 && (
                  <div style={{gridColumn:'1 / -1',fontSize:'0.85rem',color:'var(--truva-text-tertiary)',padding:'8px 0'}}>
                    No time slots yet — add one under Attendant week.
                  </div>
                )}
                {roster.slots.map(s => (
                  <React.Fragment key={s.start}>
                    <div style={{fontSize:'0.8rem',color:'var(--truva-text-secondary)'}}>
                      {s.label}
                      <div style={{fontSize:'0.72rem',color:'var(--truva-text-tertiary)'}}>{s.start}-{s.end}</div>
                    </div>
                    {roster.days.map(day => {
                      const key = `${day.date}|${s.start}`;
                      return (
                        <select key={key} value={rosterDraft[key] || ''} disabled={!canEditScheduling}
                          onChange={e=>setRosterDraft(prev=>({...prev,[key]:e.target.value}))}
                          style={{padding:'6px 4px',fontSize:'0.8rem',borderRadius:6,border:'1px solid var(--truva-border)',background:'var(--truva-surface-2)',color:'var(--truva-text)'}}>
                          <option value="">--</option>
                          {(roster.resources||[]).map(res => <option key={res.id} value={String(res.id)}>{res.name}</option>)}
                        </select>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          <h4 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Staff / Resources</h4>
          <p style={{margin:'0 0 10px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
            Each resource is a person, room, or calendar the AI can book against — e.g. an attendant on shift,
            or a single consultant's calendar. A service type below can be offered by one or more of these.
          </p>
          <div style={{display:'flex',gap:8,marginBottom:12}}>
            <input style={IS} placeholder="e.g. Attendant, Consultant" value={newResourceName}
              onChange={e=>setNewResourceName(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();handleAddResource();}}} />
            <button type="button" style={BTN} onClick={handleAddResource}>Add</button>
          </div>

          {resources.filter(r => r.active !== false).map(res => (
            <div key={res.id} style={{border:'1px solid var(--truva-border)',borderRadius:8,padding:'10px 12px',marginBottom:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <input style={{...IS,marginBottom:0,fontWeight:600}} value={res.name}
                  onChange={e=>handleResourceNameLocalChange(res.id, e.target.value)}
                  onBlur={e=>handleRenameResource(res.id, e.target.value)} />
                <button type="button"
                  style={{...BTN,backgroundColor:editingHoursFor===res.id?'var(--truva-btn-secondary-bg)':'var(--truva-btn-secondary-bg)',padding:'6px 10px',fontSize:'0.88rem',whiteSpace:'nowrap'}}
                  onClick={()=>setEditingHoursFor(editingHoursFor===res.id?null:res.id)}>
                  {editingHoursFor===res.id?'Close Hours':'Edit Hours'}
                </button>
                <button type="button"
                  style={{...BTN,backgroundColor:'var(--truva-danger-red)',padding:'6px 10px',fontSize:'0.88rem'}}
                  onClick={()=>handleDeleteResource(res.id, res.name)}>Delete</button>
              </div>
              {editingHoursFor===res.id && (
                <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--truva-border)'}}>
                  {HOURS_DAYS.map(day => {
                    const d = res.hours?.[day] || {};
                    return (
                      <div key={day} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0'}}>
                        <span style={{width:36,fontSize:'0.95rem',color:'var(--truva-text)'}}>{HOURS_DAY_LABELS[day]}</span>
                        <label style={{display:'flex',alignItems:'center',gap:4,fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>
                          <input type="checkbox" checked={!!d.closed}
                            onChange={e=>handleResourceHoursDayChange(res.id, day, 'closed', e.target.checked)} />
                          Closed
                        </label>
                        <input type="time" value={d.open || '09:00'} disabled={!!d.closed}
                          onChange={e=>handleResourceHoursDayChange(res.id, day, 'open', e.target.value)}
                          style={{padding:4,fontSize:'0.9rem',opacity:d.closed?0.5:1}} />
                        <span style={{fontSize:'0.9rem',color:'var(--truva-text-tertiary)'}}>to</span>
                        <input type="time" value={d.close || '17:00'} disabled={!!d.closed}
                          onChange={e=>handleResourceHoursDayChange(res.id, day, 'close', e.target.value)}
                          style={{padding:4,fontSize:'0.9rem',opacity:d.closed?0.5:1}} />
                      </div>
                    );
                  })}
                  <button type="button" style={{...BTN,marginTop:10}} onClick={()=>handleSaveResourceHours(res.id)}>Save Hours</button>
                </div>
              )}
            </div>
          ))}
          {resources.length === 0 && <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.95rem',padding:'8px 0'}}>No resources yet — add one above.</p>}

          <h4 style={{margin:'20px 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Service Types</h4>
          <p style={{margin:'0 0 10px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
            What a customer can book — each has its own duration and which resource(s) can fulfill it.
          </p>
          <div style={{border:'1px solid var(--truva-border)',padding:10,borderRadius:6,marginBottom:12}}>
            <input style={IS} placeholder="Service name (e.g. Wash & Fold Drop-off)" value={newSchedService.name}
              onChange={e=>setNewSchedService({...newSchedService,name:e.target.value})} />
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
              <span style={{fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>Duration</span>
              <input type="number" min={5} step={5} style={{...IS,width:80,marginBottom:0}}
                value={newSchedService.duration_minutes}
                onChange={e=>setNewSchedService({...newSchedService,duration_minutes:e.target.value})} />
              <span style={{fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>minutes</span>
            </div>
            {resources.length > 0 && (
              <div style={{marginBottom:8}}>
                <span style={{fontSize:'0.9rem',color:'var(--truva-text-secondary)',display:'block',marginBottom:4}}>Fulfilled by</span>
                {resources.filter(r => r.active !== false).map(res => (
                  <label key={res.id} style={{display:'flex',alignItems:'center',gap:8,padding:'2px 0',cursor:'pointer'}}>
                    <input type="checkbox" checked={newSchedService.resource_ids.includes(res.id)}
                      onChange={()=>toggleNewServiceResource(res.id)} />
                    <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>{res.name}</span>
                  </label>
                ))}
              </div>
            )}
            <button type="button" style={BTN} onClick={handleAddSchedService}>Add Service Type</button>
          </div>
          {schedServices.map(svc => (
            <div key={svc.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid var(--truva-border)'}}>
              <div>
                <div style={{fontWeight:600,fontSize:'0.95rem',color:'var(--truva-text)'}}>{svc.name} <span style={{fontWeight:400,color:'var(--truva-text-tertiary)'}}>· {svc.duration_minutes} min</span></div>
                <div style={{fontSize:'0.85rem',color:'var(--truva-text-tertiary)',marginTop:2}}>
                  {svc.resource_ids.length === 0 ? 'No resource assigned yet' :
                    resources.filter(r => svc.resource_ids.includes(r.id)).map(r => r.name).join(', ')}
                </div>
              </div>
              <button type="button" style={{...BTN,backgroundColor:'var(--truva-danger-red)',padding:'6px 10px',fontSize:'0.88rem'}}
                onClick={()=>handleDeleteSchedService(svc.id, svc.name)}>Delete</button>
            </div>
          ))}
          {schedServices.length === 0 && <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.95rem',padding:'8px 0'}}>No service types yet — add one above.</p>}
        </section>
        </ErrorBoundary>
      )}

      {/* === APPOINTMENTS TAB (calendar view of bookings) === */}
      {tab === 'appointments' && (
        <ErrorBoundary inline label="Appointments">
        <section style={{...SS, padding:16}}>
          <h3 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Appointments</h3>
          <p style={{margin:'0 0 12px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>
            Bookings the AI makes on your lines. Configure staff and services under Scheduling.
          </p>

          <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:12}}>
            <button type="button"
              onClick={()=>setApptResourceFilter(null)}
              style={{...BTN,padding:'6px 12px',fontSize:'0.85rem',
                background:apptResourceFilter==null?'var(--truva-action-blue)':'var(--truva-btn-secondary-bg)',
                color:apptResourceFilter==null?'#fff':'var(--truva-text)'}}>
              All
            </button>
            {resources.filter(r=>r.active!==false).map(res => (
              <button key={res.id} type="button"
                onClick={()=>setApptResourceFilter(res.id)}
                style={{...BTN,padding:'6px 12px',fontSize:'0.85rem',
                  background:apptResourceFilter===res.id?'var(--truva-action-blue)':'var(--truva-btn-secondary-bg)',
                  color:apptResourceFilter===res.id?'#fff':'var(--truva-text)'}}>
                {res.name}
              </button>
            ))}
          </div>

          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <button type="button" style={{...BTN,padding:'6px 10px',background:'var(--truva-btn-secondary-bg)',color:'var(--truva-text)'}}
              onClick={()=>{ const n=new Date(apptWeekStart); n.setDate(n.getDate()-7); setApptWeekStart(n); }}>‹</button>
            <span style={{fontSize:'0.9rem',fontWeight:600,color:'var(--truva-text)'}}>
              {apptWeekStart.toLocaleDateString(undefined,{month:'short',day:'numeric'})}
              {' – '}
              {(() => { const e=new Date(apptWeekStart); e.setDate(e.getDate()+6); return e.toLocaleDateString(undefined,{month:'short',day:'numeric'}); })()}
            </span>
            <button type="button" style={{...BTN,padding:'6px 10px',background:'var(--truva-btn-secondary-bg)',color:'var(--truva-text)'}}
              onClick={()=>{ const n=new Date(apptWeekStart); n.setDate(n.getDate()+7); setApptWeekStart(n); }}>›</button>
          </div>

          {apptLoading && <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.9rem'}}>Loading…</p>}
          {!apptLoading && appointments.length === 0 && (
            <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.95rem',padding:'12px 0'}}>
              No appointments this week{apptResourceFilter ? ' for this consultant' : ''}.
            </p>
          )}
          {!apptLoading && appointments.map(a => {
            const start = a.start_time ? new Date(a.start_time) : null;
            const when = start
              ? start.toLocaleString(undefined, { weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })
              : '—';
            return (
              <div key={a.id} style={{
                display:'flex', gap:10, alignItems:'flex-start',
                border:'1px solid var(--truva-border)', borderRadius:10,
                padding:'10px 12px', marginBottom:8, background:'var(--truva-surface)',
              }}>
                <div style={{minWidth:88,fontSize:'0.8rem',fontWeight:700,color:'var(--truva-action-blue)'}}>{when}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:'0.95rem',color:'var(--truva-text)'}}>{a.customer_name || '—'}</div>
                  <div style={{fontSize:'0.85rem',color:'var(--truva-text-tertiary)',marginTop:2}}>
                    {a.service_type || 'Appointment'}
                    {a.resource_name ? ` · ${a.resource_name}` : ''}
                    {a.customer_phone ? ` · ${a.customer_phone}` : ''}
                    {a.source ? ` · via ${a.source}` : ''}
                  </div>
                </div>
                <span style={{
                  fontSize:'0.7rem', fontWeight:600, padding:'3px 8px', borderRadius:999,
                  background: a.status==='scheduled' ? 'var(--truva-success-tint)' : 'var(--truva-surface-2)',
                  color: a.status==='scheduled' ? 'var(--truva-success-green)' : 'var(--truva-text-secondary)',
                  textTransform:'capitalize', whiteSpace:'nowrap',
                }}>{a.status || 'scheduled'}</span>
                {canEditScheduling && a.status === 'scheduled' && (
                  <button type="button"
                    style={{...BTN,backgroundColor:'var(--truva-danger-red)',padding:'6px 10px',fontSize:'0.8rem'}}
                    onClick={()=>handleCancelAppointment(a.id, a.customer_name)}>
                    Cancel
                  </button>
                )}
              </div>
            );
          })}
        </section>
        </ErrorBoundary>
      )}

      {/* === TEAM TAB === */}
      {tab === 'team' && (
        <ErrorBoundary inline label="Team">
        {(() => {
        const activeCount = members.filter(m => m.status === 'active').length;
        const invitedCount = members.filter(m => m.status === 'invited').length;
        const seatMax = seatInfo.max || maxSeatsFromPlan || 1;
        const usedSeats = activeCount + invitedCount;
        const freeCount = Math.max(0, seatMax - usedSeats);
        const atCap = usedSeats >= seatMax;
        const pct = (n) => seatMax > 0 ? (n / seatMax) * 100 : 0;
        return (
        <section style={{...SS, padding:16}}>
          <h3 style={{margin:'0 0 4px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Team</h3>

          <div style={{border:'1px solid var(--truva-border)',borderRadius:10,padding:'12px 14px',marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:8}}>
              <span style={{fontSize:'0.9rem',color:'var(--truva-text-secondary)',fontWeight:600}}>Seats used</span>
              <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}><strong>{usedSeats}</strong> / {seatMax} · {freeCount} remaining</span>
            </div>
            <div style={{height:6,borderRadius:99,background:'var(--truva-surface-2)',overflow:'hidden',display:'flex'}}>
              <span style={{display:'block',height:'100%',width:pct(activeCount)+'%',background:'var(--truva-action-blue)'}} />
              <span style={{display:'block',height:'100%',width:pct(invitedCount)+'%',background:'var(--truva-warn-amber)'}} />
            </div>
            <div style={{display:'flex',gap:14,marginTop:8,fontSize:'0.82rem',color:'var(--truva-text-tertiary)'}}>
              <span><span style={{display:'inline-block',width:7,height:7,borderRadius:99,background:'var(--truva-action-blue)',marginRight:4}} />{activeCount} active</span>
              <span><span style={{display:'inline-block',width:7,height:7,borderRadius:99,background:'var(--truva-warn-amber)',marginRight:4}} />{invitedCount} invited</span>
              <span><span style={{display:'inline-block',width:7,height:7,borderRadius:99,background:'var(--truva-surface-2)',marginRight:4}} />{freeCount} free</span>
            </div>
          </div>

          <div style={{display:'flex',gap:8,marginBottom:4}}>
            <input style={IS} placeholder="Email address" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} />
            <select style={{...IS,width:'auto',marginBottom:0}} value={inviteRole} onChange={e=>setInviteRole(e.target.value)}>
              <option value="agent">Agent</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
            <button type="button" style={{...BTN, opacity: atCap ? 0.5 : 1}} disabled={atCap} onClick={async ()=>{
              if (!inviteEmail.trim() || atCap) return;
              try {
                const r = await fetch(API_BASE + '/api/team/invites', {
                  method:'POST', headers:{Authorization:'Bearer '+authToken,'Content-Type':'application/json'},
                  body:JSON.stringify({email:inviteEmail.trim(),role:inviteRole})
                });
                if (r.ok) { setInviteEmail(''); loadMembers(); setSaveStatus('Invited'); setTimeout(()=>setSaveStatus(''),2000); }
                else { const d=await r.json(); setError(d.message||d.error||'Invite failed'); }
              } catch { setError('Invite failed'); }
            }}>Invite</button>
          </div>
          <p style={{margin:'0 0 16px',fontSize:'0.82rem',color:'var(--truva-text-tertiary)',lineHeight:1.5}}>
            <strong>Admin</strong> can invite, remove, and change line settings (Profile, Agent, Chatbot, Translate). <strong>Manager</strong> can view and edit Scheduling and Appointments but cannot manage the team or change line settings. <strong>Agent</strong> can make and receive calls/texts on assigned lines but cannot change settings or manage the team.
            {atCap ? ' This plan has no seats left — upgrade to Business (5 seats) or cancel an invite.' : ''}
          </p>

          <div style={{borderTop:'1px solid var(--truva-border)',paddingTop:8}}>
            {members.map(m => {
              const isActive = m.status === 'active';
              const initial = (m.email || '?').charAt(0).toUpperCase();
              return (
              <div key={m.id} style={{padding:'10px 0',borderBottom:'1px solid var(--truva-border)'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{
                    width:32,height:32,borderRadius:9,flexShrink:0,
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontSize:'0.9rem',fontWeight:700,color:'var(--truva-on-action)',
                    background: isActive ? 'var(--truva-action-blue)' : 'transparent',
                    border: isActive ? 'none' : '1.5px dashed var(--truva-border)',
                    ...(isActive ? {} : {color:'var(--truva-warn-text)'}),
                  }}>{isActive ? initial : '?'}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:'0.95rem',color:'var(--truva-text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{m.email}</div>
                    <div style={{display:'flex',gap:5,marginTop:3,flexWrap:'wrap',alignItems:'center'}}>
                      {/* Roles predate the Agent/Admin invite system (owner/user
                          are legacy values from the old SSO bug and the old
                          password-signup path). Always show the current
                          two-value vocabulary here so the UI doesn't leak raw
                          legacy strings the invite dropdown never produces;
                          the raw role is preserved as-is in the DB and drives
                          Make Admin/Make Agent below. */}
                      <span style={{
                        fontSize:'0.75rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.02em',
                        padding:'2px 7px',borderRadius:99,
                        background: m.role === 'admin' || m.role === 'owner'
                          ? 'var(--truva-info-tint)'
                          : (m.role === 'manager' ? 'var(--truva-surface-2)' : 'var(--truva-surface-2)'),
                        color: m.role === 'admin' || m.role === 'owner'
                          ? 'var(--truva-action-blue)'
                          : (m.role === 'manager' ? 'var(--truva-text)' : 'var(--truva-text-secondary)'),
                        border: m.role === 'manager' ? '1px solid var(--truva-border)' : 'none',
                      }}>{m.role === 'admin' || m.role === 'owner' ? 'Admin' : (m.role === 'manager' ? 'Manager' : 'Agent')}</span>
                      <span style={{
                        fontSize:'0.75rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.02em',
                        padding:'2px 7px',borderRadius:99,
                        background: isActive ? 'var(--truva-success-tint-chip)' : 'var(--truva-warn-tint-chip)',
                        color: isActive ? 'var(--truva-success-text)' : 'var(--truva-warn-text)',
                      }}>{isActive ? 'Active' : 'Invited'}</span>
                    </div>
                    {isActive && (
                      <div style={{fontSize:'0.82rem',color:'var(--truva-text-tertiary)',marginTop:4}}>
                        {m.numbers && m.numbers.length > 0
                          ? m.numbers.map(n => displayPhoneNumber(n.e164)).join(', ')
                          : 'All numbers'}
                      </div>
                    )}
                  </div>
                  <div style={{position:'relative',flexShrink:0}}>
                    <button type="button" onClick={(e)=>{
                      if (openMemberMenu === m.id) { setOpenMemberMenu(null); return; }
                      const r = e.currentTarget.getBoundingClientRect();
                      const MENU_H = 170; // up to 3 items + padding
                      const flipUp = r.bottom + 4 + MENU_H > window.innerHeight && r.top > MENU_H;
                      setMemberMenuPos({
                        right: Math.max(8, window.innerWidth - r.right),
                        ...(flipUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
                      });
                      setOpenMemberMenu(m.id);
                    }}
                      style={{width:28,height:28,border:'1px solid var(--truva-border)',borderRadius:8,background:'var(--truva-surface)',cursor:'pointer',fontSize:'1rem',color:'var(--truva-text-secondary)',lineHeight:1}}>
                      &#8942;
                    </button>
                    {openMemberMenu === m.id && (
                      <div style={{
                        position:'fixed',...(memberMenuPos || {right:8,top:0}),background:'var(--truva-surface)',border:'1px solid var(--truva-border)',
                        borderRadius:8,boxShadow:'0 4px 16px var(--truva-elevation-e1)',minWidth:150,zIndex:50,padding:4,
                      }}>
                        {isActive && (
                          <button type="button" onClick={()=>{setOpenMemberMenu(null); openNumbersEditor(m.id);}}
                            style={{display:'block',width:'100%',textAlign:'left',padding:'7px 9px',fontSize:'0.9rem',color:'var(--truva-text)',background:'none',border:'none',borderRadius:6,cursor:'pointer'}}>
                            Edit numbers
                          </button>
                        )}
                        {isActive && !(m.role === 'admin' || m.role === 'owner') && (
                          <button type="button" onClick={()=>changeMemberRole(m.id,'admin')}
                            style={{display:'block',width:'100%',textAlign:'left',padding:'7px 9px',fontSize:'0.9rem',color:'var(--truva-text)',background:'none',border:'none',borderRadius:6,cursor:'pointer'}}>
                            Make Admin
                          </button>
                        )}
                        {isActive && m.role !== 'manager' && (
                          <button type="button" onClick={()=>changeMemberRole(m.id,'manager')}
                            style={{display:'block',width:'100%',textAlign:'left',padding:'7px 9px',fontSize:'0.9rem',color:'var(--truva-text)',background:'none',border:'none',borderRadius:6,cursor:'pointer'}}>
                            Make Manager
                          </button>
                        )}
                        {isActive && m.role !== 'agent' && (
                          <button type="button" onClick={()=>changeMemberRole(m.id,'agent')}
                            style={{display:'block',width:'100%',textAlign:'left',padding:'7px 9px',fontSize:'0.9rem',color:'var(--truva-text)',background:'none',border:'none',borderRadius:6,cursor:'pointer'}}>
                            Make Agent
                          </button>
                        )}
                        <button type="button" onClick={async()=>{
                          setOpenMemberMenu(null);
                          try {
                            const r = await fetch(API_BASE + '/api/team/members/' + m.id, {method:'DELETE',headers:{Authorization:'Bearer '+authToken}});
                            const d = await r.json();
                            if (!r.ok) setError(d.error || 'Remove failed');
                            loadMembers();
                          } catch { setError('Remove failed'); }
                        }} style={{display:'block',width:'100%',textAlign:'left',padding:'7px 9px',fontSize:'0.9rem',background:'none',border:'none',borderRadius:6,cursor:'pointer',color:'var(--truva-danger-text)'}}>
                          {isActive ? 'Remove from team' : 'Cancel invite'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {numbersEditingFor === m.id && (
                  <div style={{marginTop:8,padding:10,border:'1px solid var(--truva-border)',borderRadius:6,background:'var(--truva-surface)'}}>
                    <p style={{margin:'0 0 8px',fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>
                      Check the numbers this person can use. Leave all unchecked for unrestricted access to every number.
                    </p>
                    {numberOptions.map(opt => (
                      <label key={opt.id} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',cursor:'pointer'}}>
                        <input type="checkbox" checked={selectedNumberIds.includes(opt.id)}
                          onChange={()=>toggleNumberSelection(opt.id)} />
                        <span style={{fontSize:'0.95rem',color:'var(--truva-text)'}}>{opt.label || opt.e164}</span>
                      </label>
                    ))}
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button type="button" disabled={numbersBusy} onClick={saveNumbersEditor}
                        style={{...BTN,padding:'6px 12px',fontSize:'0.9rem'}}>Save</button>
                      <button type="button" onClick={()=>setNumbersEditingFor(null)}
                        style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',padding:'6px 12px',fontSize:'0.9rem'}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
            {members.length === 0 && <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.95rem',textAlign:'center',padding:20}}>No team members yet.</p>}
          </div>
        </section>
        );
        })()}
        </ErrorBoundary>
      )}

      {/* === NUMBERS & LINES TAB === */}
      {tab === 'numbers' && (
        <ErrorBoundary inline label="Numbers">
        <>
        {numbersError && <div style={{color:'var(--truva-danger-text)',fontSize:'0.95rem',margin:'0 0 6px 0'}}>{numbersError}</div>}
        <section style={{...SS, padding:16}}>
          <h3 style={{margin:'0 0 4px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Numbers &amp; Lines</h3>
          <p style={{margin:'0 0 12px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
            {isAdmin
              ? 'Manage the phone numbers on your account — request new ones, rename them, set which line is the default, or close ones you no longer need.'
              : 'See the phone numbers on your account. Only admins can request or change them — if you need a new number, ask an admin.'}
          </p>

          <h4 style={{margin:'0 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Your numbers</h4>
          {numbersLoading && <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.9rem',padding:'6px 0'}}>Loading numbers…</p>}
          {!numbersLoading && numbers.length === 0 && <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.95rem',padding:'6px 0'}}>
            {isAdmin ? 'No numbers on this account yet.' : 'No phone number is assigned to you yet. Ask an admin to assign or request one.'}
          </p>}
          {numbers.map(n => {
            const rowKey = n.id || n.phone_number;
            const caps = n.capabilities || {};
            const isCurrent = normPhone(selectedNumber) !== '' && normPhone(selectedNumber) === normPhone(n.phone_number);
            const isEditing = editingNumberFor === rowKey;
            const busy = editBusy === rowKey;
            const actionsLocked = !!defaultBusy || !!deleteBusy || !!editBusy;
            return (
              <div key={rowKey} style={{border:'1px solid var(--truva-border)',borderRadius:8,padding:'10px 12px',marginBottom:8,background:'var(--truva-surface)'}}>
                {/* Identity (name/number) always its own row, full width --
                    it used to share a flex row with the badges, so a short
                    heading (a bare number with no friendly_name) let badges
                    crowd up against it inconsistently depending on text
                    length. Status/actions are a second row below, always. */}
                <div style={{marginBottom:8}}>
                  <div style={{fontWeight:600,fontSize:'0.95rem',color:'var(--truva-text)'}}>{n.friendly_name || displayPhoneNumber(n.phone_number)}</div>
                  {/* Skip the sub-line when the heading is already just the
                      number (no real friendly_name set) -- showing it twice
                      was the other half of "this card looks different from
                      the others" once formatting alone was made consistent. */}
                  {n.friendly_name && (
                    <div style={{fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>{displayPhoneNumber(n.phone_number)}</div>
                  )}
                </div>
                {/* Badges and action buttons are always their own separate
                    full-width rows, never sharing one flex row -- that's
                    what let a card with fewer badges (more spare room) lay
                    them out inline with the buttons, while a card with more
                    badges wrapped them below, so cards looked structurally
                    different from each other depending on content alone. */}
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{display:'flex',gap:5,flexWrap:'wrap',alignItems:'center'}}>
                    {!!caps.voice && <span style={CAP_BADGE}>Voice</span>}
                    {/* No separate MMS badge -- confirmed live across every
                        number on the account that SMS and MMS capability
                        always move together, so it never carries
                        information the SMS badge doesn't already show. */}
                    {!!caps.sms && <span style={{...CAP_BADGE,background:'var(--truva-success-tint-chip)',color:'var(--truva-success-text)'}}>SMS</span>}
                    {!!n.is_default && <span style={{...CAP_BADGE,background:'var(--truva-danger-tint-chip)',color:'var(--truva-danger-text)'}}>Default</span>}
                    {isCurrent && <span style={{...CAP_BADGE,background:'var(--truva-info-tint)',color:'var(--truva-action-blue)'}}>Current</span>}
                    {!caps.sms && (() => {
                      const req = smsReqStatus(n.phone_number);
                      const st = req && req.status;
                      if (st) {
                        const colors = {
                          pending:      {bg:'var(--truva-warn-tint-chip)', c:'var(--truva-warn-text)'},
                          submitted:    {bg:'var(--truva-info-tint)', c:'var(--truva-action-blue)'},
                          approved:     {bg:'var(--truva-success-tint-chip)', c:'var(--truva-success-text)'},
                          rejected:     {bg:'var(--truva-danger-tint-chip)', c:'var(--truva-danger-text)'},
                        }[st] || {bg:'var(--truva-surface-2)', c:'var(--truva-text-secondary)'};
                        const labels = {
                          pending: 'SMS pending review',
                          submitted: 'SMS submitted',
                          approved: 'SMS approved',
                          rejected: 'SMS request rejected',
                        }[st] || st;
                        return (
                          <>
                            <span title={req.admin_notes || ''}
                              style={{...CAP_BADGE, background:colors.bg, color:colors.c}}>{labels}</span>
                            {isAdmin && (
                            <button type="button"
                              onClick={()=>{ setSmsReqFor(n.phone_number); setSmsError(''); setSmsForm(smsFormFromReq(req)); }}
                              style={{...BTN, background:'var(--truva-btn-secondary-bg)', padding:'4px 8px', fontSize:'0.8rem'}}>Edit request</button>
                            )}
                          </>
                        );
                      }
                      return isAdmin ? (
                        <button type="button"
                          onClick={()=>{ setSmsReqFor(n.phone_number); setSmsError(''); setSmsForm(smsFormFromReq(null)); }}
                          style={{...BTN, background:'var(--truva-success-green)', padding:'4px 8px', fontSize:'0.8rem'}}>Request SMS</button>
                      ) : null;
                    })()}
                  </div>
                  {isAdmin && (
                  <div style={{display:'flex',gap:6,flexShrink:0}}>
                    <button type="button" disabled={actionsLocked} onClick={()=>handleSetDefault(n)}
                      style={{...BTN,backgroundColor:'var(--truva-action-blue)',padding:'5px 9px',fontSize:'0.85rem'}}>
                      {defaultBusy === rowKey ? '…' : 'Set default'}
                    </button>
                    <button type="button" disabled={actionsLocked} onClick={()=>{setEditingNumberFor(isEditing ? null : rowKey); setEditingLabel(n.friendly_name || n.phone_number);}}
                      style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',padding:'5px 9px',fontSize:'0.85rem'}}>
                      Edit
                    </button>
                    <button type="button" disabled={actionsLocked} onClick={()=>handleCloseNumber(n)}
                      style={{...BTN,backgroundColor:'var(--truva-danger-red)',padding:'5px 9px',fontSize:'0.85rem'}}>
                      {deleteBusy === rowKey ? '…' : 'Close'}
                    </button>
                  </div>
                  )}
                </div>
                {isEditing && (
                  <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid var(--truva-border)'}}>
                    <input style={IS} placeholder="Label (e.g. Main line, Front desk)" value={editingLabel} onChange={e=>setEditingLabel(e.target.value)} />
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button type="button" disabled={busy} onClick={()=>saveNumberEdit(n)}
                        style={{...BTN,padding:'6px 12px',fontSize:'0.9rem'}}>{busy?'Saving…':'Save label'}</button>
                      <button type="button" onClick={()=>setEditingNumberFor(null)}
                        style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)',padding:'6px 12px',fontSize:'0.9rem'}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {isAdmin && <>
          <h4 style={{margin:'18px 0 6px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Request a new number</h4>
          <p style={{margin:'0 0 10px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)'}}>Search by US area code, then pick an available number to add to your account.</p>
          <div style={{display:'flex',gap:8,marginBottom:10}}>
            <input style={{...IS,width:110,marginBottom:0}} placeholder="Area code" value={searchArea} maxLength={3}
              onChange={e=>{setSearchArea(e.target.value.replace(/[^0-9]/g,'').slice(0,3)); setNumbersSearched(false);}}
              onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();handleSearchNumbers();}}} />
            <button type="button" disabled={searching} style={BTN} onClick={handleSearchNumbers}>{searching?'Searching…':'Search'}</button>
          </div>
          {numbersSearched && searchResults.length === 0 && (
            <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.9rem',padding:'4px 0'}}>{searchFallback || 'No numbers matched that area code — try another.'}</p>
          )}
          {searchFallback && searchResults.length > 0 && (
            <p style={{color:'var(--truva-warn-text)',fontSize:'0.9rem',padding:'4px 0'}}>{searchFallback}</p>
          )}
          {searchResults.length > 0 && (
            <div style={{border:'1px solid var(--truva-border)',borderRadius:6,padding:10,marginBottom:10}}>
              <label style={{fontSize:'0.9rem',color:'var(--truva-text-secondary)',display:'block',marginBottom:4}}>Available numbers</label>
              <select style={{...IS,marginBottom:0}} value={selectedAvailable} onChange={e=>{setSelectedAvailable(e.target.value);setNumbersError('');}}>
                <option value="">Select a number…</option>
                {searchResults.map(r => (
                  <option key={r.phone_number} value={r.phone_number}>{r.phone_number}{r.type === 'tollfree' ? ' — Toll-free' : (r.locality ? ` — ${r.locality}${r.region ? ', ' + r.region : ''}` : '')}</option>
                ))}
              </select>
              <input style={{...IS,marginTop:8}} placeholder="Label (optional)" value={requestLabel} onChange={e=>setRequestLabel(e.target.value)} />
              <button type="button" disabled={requesting || !selectedAvailable} style={{...BTN,marginTop:10}}
                onClick={handleRequestNumber}>{requesting?'Requesting…':'Request Number'}</button>
            </div>
          )}
          </>}
        </section>

        {/* Notification permission (iOS). The system prompt is only shown
            once, so when the user has denied it this is the only place that
            tells them alerts (voicemail, texts, missed calls) will not appear
            until they re-enable it in iOS Settings. */}
        {notificationPermission === 'denied' && (
          <section style={{...SS, padding:16}}>
            <h3 style={{margin:'0 0 4px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Notifications are off</h3>
            <p style={{margin:0,fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
              Truva can&apos;t show voicemail, text, missed-call or call-summary alerts on this
              phone. Turn them on in iOS Settings &rsaquo; Truva &rsaquo; Notifications
              (Allow Notifications), then reopen the app.
            </p>
          </section>
        )}

        {/* Desk phone — a physical IP phone (Yealink T31W) gets its own SIP
            login so it rings for this user's calls alongside the app. */}
        <section style={{...SS, padding:16}}>
          <h3 style={{margin:'0 0 4px',fontSize:'0.95rem',color:'var(--truva-text)'}}>Desk phone</h3>
          <p style={{margin:'0 0 12px',fontSize:'0.9rem',color:'var(--truva-text-secondary)',lineHeight:1.4}}>
            Connect a physical IP phone (e.g. Yealink T31W). It rings for your incoming calls
            alongside the app, and calls you place from it go out as your own business line.
          </p>

          {deskPhoneError && <p style={{color:'var(--truva-danger-text)',fontSize:'0.9rem',margin:'0 0 8px'}}>{deskPhoneError}</p>}
          {deskPhoneLoading && <p style={{color:'var(--truva-text-tertiary)',fontSize:'0.9rem',padding:'6px 0'}}>Checking desk phone…</p>}

          {!deskPhoneLoading && deskPhone && (
            <>
              <div style={{display:'flex',gap:5,flexWrap:'wrap',alignItems:'center',marginBottom:10}}>
                <span style={{
                  ...CAP_BADGE,
                  background: deskPhone.configured ? 'var(--truva-success-tint-chip)' : 'var(--truva-surface-2)',
                  color: deskPhone.configured ? 'var(--truva-success-text)' : 'var(--truva-text-secondary)',
                }}>{deskPhone.configured ? 'Set up' : 'Not set up'}</span>
                {deskPhone.caller_line && (
                  <span style={{fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>
                    Calls out as {displayPhoneNumber(deskPhone.caller_line)}
                  </span>
                )}
              </div>

              {!deskPhone.password && (
                <button type="button" disabled={deskPhoneBusy} onClick={handleDeskPhoneProvision} style={BTN}>
                  {deskPhoneBusy ? 'Working…' : (deskPhone.configured ? 'Reset password' : 'Set up desk phone')}
                </button>
              )}

              {!!deskPhone.password && (
                <div style={{border:'1px solid var(--truva-border)',borderRadius:8,padding:'10px 12px',background:'var(--truva-surface)'}}>
                  <div style={{fontWeight:600,fontSize:'0.95rem',color:'var(--truva-text)',marginBottom:2}}>Enter these on the phone now</div>
                  <div style={{fontSize:'0.85rem',color:'var(--truva-warn-text)',marginBottom:10,lineHeight:1.4}}>
                    The password is shown only once. Resetting it later disconnects the phone until you enter the new one.
                  </div>
                  {[
                    ['server', 'Server', deskPhone.server],
                    ['username', 'Username', deskPhone.username],
                    ['password', 'Password', deskPhone.password],
                  ].map(([key, label, value]) => (
                    <div key={key} style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
                      <div style={{flex:'0 0 78px',fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>{label}</div>
                      <code style={{flex:1,minWidth:0,overflowX:'auto',whiteSpace:'nowrap',fontSize:'0.9rem',color:'var(--truva-text)',background:'var(--truva-surface-2)',padding:'4px 6px',borderRadius:4}}>{value || '—'}</code>
                      <button type="button" onClick={()=>copyDeskPhoneField(key, value)}
                        style={{...BTN,minHeight:'32px',padding:'0 12px',fontSize:'0.85rem',backgroundColor:'var(--truva-btn-secondary-bg)'}}>
                        {deskPhoneCopied === key ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  ))}
                  <div style={{marginTop:10,fontSize:'0.9rem',color:'var(--truva-text-secondary)'}}>
                    <div style={{fontWeight:600,color:'var(--truva-text)',marginBottom:4}}>Yealink setup</div>
                    <ol style={{margin:0,paddingLeft:18,lineHeight:1.5}}>
                      {(deskPhone.instructions || []).map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
        </>
        </ErrorBoundary>
      )}
      {smsReqFor && (
        <div style={{position:'fixed',inset:0,background:'var(--truva-overlay-backdrop)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200}}>
          <div style={{background:'var(--truva-surface)',borderRadius:10,padding:20,width:'100%',maxWidth:520,maxHeight:'92vh',overflowY:'auto'}}>
            <h3 style={{margin:'0 0 4px',fontSize:'1rem'}}>Request SMS on {smsReqFor}</h3>
            <p style={{margin:'0 0 12px',fontSize:'0.85rem',color:'var(--truva-text-tertiary)',lineHeight:1.4}}>
              Toll-free numbers need carrier verification before texting works. The info below is what the
              carrier requires — we'll submit it as your verification request (typically 1–3 business days to approve).
            </p>

            <div style={{fontSize:'0.88rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.03em',color:'var(--truva-success-text)',margin:'0 0 6px'}}>1 · Company</div>
            <input style={IS} placeholder="Legal company name *" value={smsForm.company_name} onChange={e=>setSmsForm({...smsForm,company_name:e.target.value})} />
            <input style={IS} placeholder="Company address (full physical) *" value={smsForm.company_address} onChange={e=>setSmsForm({...smsForm,company_address:e.target.value})} />
            <input style={IS} placeholder="Company website URL *" value={smsForm.company_url} onChange={e=>setSmsForm({...smsForm,company_url:e.target.value})} />
            <div style={{display:'flex',gap:8}}>
              <input style={{...IS,flex:1}} placeholder="Compliance contact name *" value={smsForm.contact_name} onChange={e=>setSmsForm({...smsForm,contact_name:e.target.value})} />
              <input style={{...IS,flex:1}} placeholder="Contact email *" value={smsForm.contact_email} onChange={e=>setSmsForm({...smsForm,contact_email:e.target.value})} />
            </div>
            <div style={{display:'flex',gap:8}}>
              <input style={{...IS,flex:1}} placeholder="Contact phone *" value={smsForm.contact_phone} onChange={e=>setSmsForm({...smsForm,contact_phone:e.target.value})} />
              <select style={{...IS,flex:1}} value={smsForm.entity_type} onChange={e=>setSmsForm({...smsForm,entity_type:e.target.value})}>
                <option value="">Entity type *</option>
                <option>Public Profit</option><option>Private Profit</option>
                <option>Non-Profit</option><option>Government</option>
                <option>Sole Proprietor (requires additional vetting)</option>
              </select>
            </div>
            <div style={{display:'flex',gap:8}}>
              <input style={{...IS,flex:1}} placeholder="Business registration number (EIN) *" value={smsForm.business_reg_number} onChange={e=>setSmsForm({...smsForm,business_reg_number:e.target.value})} />
              <input style={{...IS,flex:1}} placeholder="Reg. issuing country *" value={smsForm.reg_country} onChange={e=>setSmsForm({...smsForm,reg_country:e.target.value})} />
            </div>
            <select style={IS} value={smsForm.fortune500} onChange={e=>setSmsForm({...smsForm,fortune500:e.target.value})}>
              <option value="">Fortune 500 / 1000 company? *</option>
              <option>Yes</option><option>No</option>
            </select>

            <div style={{fontSize:'0.88rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.03em',color:'var(--truva-success-text)',margin:'14px 0 6px'}}>2 · Messaging</div>
            <select style={IS} value={smsForm.use_case} onChange={e=>setSmsForm({...smsForm,use_case:e.target.value,use_case_text:''})}>
              <option value="reception">AI receptionist replies to customer texts</option>
              <option value="service">Customer service / support</option>
              <option value="appointments">Appointment &amp; booking reminders</option>
              <option value="ordering">Order / status updates</option>
              <option value="other">Other</option>
            </select>
            <textarea style={{...IS,minHeight:56}} placeholder="Describe the intended use case in your own words *" value={smsForm.use_case_text} onChange={e=>setSmsForm({...smsForm,use_case_text:e.target.value})} />
            <textarea style={{...IS,minHeight:60}} placeholder="Sample messages (2–3, one per line) *" value={smsForm.sample_messages} onChange={e=>setSmsForm({...smsForm,sample_messages:e.target.value})} />
            <div style={{display:'flex',gap:8}}>
              <select style={{...IS,flex:1}} value={smsForm.advertises_product} onChange={e=>setSmsForm({...smsForm,advertises_product:e.target.value})}>
                <option value="">Advertising a product/service? *</option>
                <option>Yes</option><option>No</option>
              </select>
              <select style={{...IS,flex:1}} value={smsForm.previous_provider} onChange={e=>setSmsForm({...smsForm,previous_provider:e.target.value})}>
                <option value="">Traffic from another provider? *</option>
                <option>Yes</option><option>No</option>
              </select>
            </div>
            <input style={IS} placeholder="Where is the number advertised? (or N/A) *" value={smsForm.advertised_where} onChange={e=>setSmsForm({...smsForm,advertised_where:e.target.value})} />
            <div style={{display:'flex',gap:8}}>
              <select style={{...IS,flex:1}} value={smsForm.volume} onChange={e=>setSmsForm({...smsForm,volume:e.target.value})}>
                <option value="low">Volume: Low (under ~500/mo)</option>
                <option value="medium">Volume: Medium (~500–5,000/mo)</option>
                <option value="high">Volume: High (5,000+/mo)</option>
              </select>
              <select style={{...IS,flex:1}} value={smsForm.throughput} onChange={e=>setSmsForm({...smsForm,throughput:e.target.value})}>
                <option>3 MPS (Default)</option>
                <option>10 MPS ($25/mth)</option>
                <option>50 MPS ($200/mth)</option>
                <option>100 MPS ($300/mth)</option>
              </select>
            </div>
            <div style={{display:'flex',gap:8}}>
              <select style={{...IS,flex:1}} value={smsForm.sms_mms} onChange={e=>setSmsForm({...smsForm,sms_mms:e.target.value})}>
                <option>SMS</option><option>MMS</option><option>SMS and MMS</option>
              </select>
              <select style={{...IS,flex:1}} value={smsForm.canada_primary} onChange={e=>setSmsForm({...smsForm,canada_primary:e.target.value})}>
                <option>No</option><option>Yes</option>
              </select>
            </div>

            <div style={{fontSize:'0.88rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.03em',color:'var(--truva-success-text)',margin:'14px 0 6px'}}>3 · Compliance</div>
            <textarea style={{...IS,minHeight:52}} placeholder="Opt-in message template — how users consent (e.g. 'Reply YES to receive texts from us.') *" value={smsForm.opt_in_template} onChange={e=>setSmsForm({...smsForm,opt_in_template:e.target.value})} />
            <textarea style={{...IS,minHeight:48}} placeholder="Opt-out process (e.g. 'Reply STOP to opt out at any time.') *" value={smsForm.opt_out_process} onChange={e=>setSmsForm({...smsForm,opt_out_process:e.target.value})} />
            <textarea style={{...IS,minHeight:48}} placeholder="Help messaging template (e.g. 'Reply HELP for help.') *" value={smsForm.help_template} onChange={e=>setSmsForm({...smsForm,help_template:e.target.value})} />
            <textarea style={{...IS,minHeight:52}} placeholder="Terms &amp; conditions related to messaging *" value={smsForm.terms} onChange={e=>setSmsForm({...smsForm,terms:e.target.value})} />
            <input style={IS} placeholder="Privacy policy URL *" value={smsForm.privacy_policy_url} onChange={e=>setSmsForm({...smsForm,privacy_policy_url:e.target.value})} />
            <input style={IS} placeholder="Additional call-to-action in message? (describe, or leave blank)" value={smsForm.additional_cta} onChange={e=>setSmsForm({...smsForm,additional_cta:e.target.value})} />

            {smsError && <p style={{color:'var(--truva-danger-text)',fontSize:'0.9rem',margin:'0 0 8px'}}>{smsError}</p>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:6}}>
              <button type="button" disabled={smsBusy} onClick={()=>setSmsReqFor(null)} style={{...BTN,backgroundColor:'var(--truva-btn-secondary-bg)'}}>Cancel</button>
              <button type="button" disabled={smsBusy || !(smsForm.sample_messages.trim()) || !(smsForm.company_name.trim())} onClick={handleSubmitSmsRequest} style={{...BTN}}>{smsBusy?'Submitting…':'Request SMS'}</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
